export default {
  page_title: 'RecoveryAI',
  // Announced while the route waits for LaunchDarkly to evaluate the merchant's
  // context — sighted users see the spinner, this is its screen-reader label.
  loading: 'Loading RecoveryAI',
  // Static marketing landing page shown at /recovery_ai until RecoveryAI is
  // active for the merchant. Numbers are placeholder copy from the Figma spec;
  // they get wired to real merchant data in a follow-up.
  promo: {
    hero: {
      title: 'Win more guests back by solving issues the moment they happen',
      subtitle:
        'Third-party platforms already auto-resolve complaints on your behalf, spending your dollars without your input. RecoveryAI puts you in control of recovery for your own ordering channels.',
      phone_alt: 'Preview of RecoveryAI resolving a guest issue',
    },
    losing_guests: {
      title: "Recover guests you're losing today",
      subtitle:
        "With RecoveryAI in your corner, more of the guests who'd normally walk away come back instead — recovered automatically, before a bad experience turns into a lost customer.",
      widget: {
        title: 'Customers won back',
        with_recovery: 'With RecoveryAI',
        without_recovery: 'Without RecoveryAI',
        with_value: '54%',
        without_value: '42%',
      },
    },
    instant_recovery: {
      title: 'Resolve every customer order issue instantly',
      subtitle:
        'RecoveryAI reviews complaints, evaluates each situation, and recommends the best resolution — automatically protecting your most valuable customer relationships.',
      eyebrow: 'Trained on real order history',
      stat: '150M+',
      // Split so "740+ brands" can render bold beside the regular-weight lead-in,
      // per Figma (node 129:1781).
      stat_caption_prefix: 'orders across',
      stat_caption_brands: '740+ brands',
      body: 'Every recommendation draws on more order history than a team could ever read — so the response is right, and it lands in seconds.',
      without_title: 'Without RecoveryAI',
      without_response_time: '2.4 days',
      without_response_rate: '40%',
      with_title: 'With RecoveryAI',
      with_response_time: '1 min',
      with_response_rate: '100%',
      response_time_label: 'Response time',
      response_rate_label: 'Response rate',
    },
    roi: {
      title: 'Recovered revenue and time saved produce clearly measurable ROI',
      upside_label: 'Projected monthly upside',
      based_on: 'Based on your data last 30 days',
      // The band's live figure: the labor and revenue chains summed.
      upside_value_live: '+%{amount}',
      per_month: '/month',
      // A native react-i18nify (base, _plural) pair: `count` must be
      // passed as a *number* — the base (singular) form is what count === 1
      // selects, and a pre-formatted string count would select the plural every
      // time. The displayed figure is the separate, thousands-separated
      // `%{locations}` so large counts still read like the rest of the section.
      per_location_live:
        '= %{amount} per location, across your %{locations} location',
      per_location_live_plural:
        '= %{amount} per location, across your %{locations} locations',
      labor_title: 'Labor cost saved',
      labor_body:
        'About %{hours} hours of General Manager time every month — no longer spent triaging complaints by hand.',
      revenue_title: 'Revenue recovered',
      revenue_body:
        'From %{count} guests who now come back instead of walking away after a bad experience.',
      see_the_math: 'See the math',
      hide_the_math: 'Hide the math',
      // Eyebrow above each "see the math" panel — "{card title} · how this is
      // calculated", rendered uppercase in SeeTheMathPanel via text-transform.
      math_eyebrow: '%{title} · how this is calculated',
      // Both chains (orders -> dissatisfied guests -> GM hours/returning guests
      // -> dollars) render the merchant's real 30-day order volume via
      // useMonthlyOrders (a real zero included -- see RoiSection.tsx's
      // liveFigures()). The `*_value` figures that remain here are static
      // product-constant rate rows (4 min, $30/hr, 38%) -- mirroring the
      // constants RoiSection multiplies by -- and the complaint-rate row
      // (interpolated, D1: it always reflects the real BE rate or the
      // DEFAULT_COMPLAINT_RATE fallback, never a hardcoded 3.5%). The average
      // check is the exception: it is the merchant's own when the endpoint
      // reports one, else the $26.57 default.
      labor_math: {
        headline: '≈ %{hours} GM hours handed back each month',
        orders_label: 'Digital orders, last 30 days',
        complaint_rate_label: 'Your thumbs-down rate',
        // Interpolated (D1): the printed rate is always the one actually
        // applied (the live BE rate, or the DEFAULT_COMPLAINT_RATE fallback
        // when it isn't computable — see RoiSection.tsx), never a static 3.5%.
        complaint_rate_value: '× %{rate}',
        dissatisfied_label: 'Dissatisfied guests',
        handling_time_label: 'Time to handle each by hand',
        // `%{minutes}` is interpolated from HANDLING_MINUTES_PER_COMPLAINT so the
        // printed row can't drift from the constant the labor chain multiplies by.
        handling_time_value: '× %{minutes} min',
        // Subtotal row between handling time and GM pay: dissatisfied guests ×
        // 4 min, expressed back in hours. The "=" lives in the label per Figma.
        reclaimed_hours_label: '= reclaimed GM hours',
        reclaimed_hours_value: '%{hours} hrs',
        hourly_pay_label: 'General Manager pay',
        // `%{pay}` is interpolated from GM_HOURLY_PAY_DOLLARS so the printed row
        // can't drift from the constant the labor chain multiplies by.
        hourly_pay_value: '× $%{pay} / hr',
        total_label: 'Labor cost saved',
        total_value: '%{amount} / month',
        // `%{pay}` is interpolated from GM_HOURLY_PAY_DOLLARS (the "30 days" here
        // is unrelated copy, left literal).
        footnote_live:
          'Based on your last 30 days of orders and feedback. Reclaimed manager time valued at a $%{pay}/hr GM wage.',
      },
      revenue_math: {
        headline: '%{count} guests came back who otherwise would have walked',
        orders_label: 'Digital orders, last 30 days',
        complaint_rate_label: 'Your thumbs-down rate',
        complaint_rate_value: '× %{rate}',
        dissatisfied_label: 'Total orders with dissatisfied guests',
        recovery_rate_label: 'Estimated return rate after RecoveryAI replies',
        // `%{rate}` is interpolated from RETURNING_RATE (via percentageFormatter)
        // so the printed row can't drift from the constant the revenue chain
        // multiplies by.
        recovery_rate_value: '× %{rate}',
        returning_label: 'Returning guests',
        check_label: 'Digital average check',
        // Product default, used when the order metrics endpoint has no measured
        // check for the merchant. `%{amount}` is interpolated from
        // DEFAULT_AVERAGE_CHECK_DOLLARS (via currencyFormatter) so the printed
        // row can't drift from the constant the revenue chain multiplies by.
        check_value: '× %{amount}',
        // Live twin: the merchant's own average check for the same 30-day
        // window, which also drives the revenue multiplication.
        check_value_live: '× %{amount}',
        total_label: 'Revenue recovered',
        total_value: '%{amount} / month',
      },
      // Per-stat deep-links (FR-8 v9.8, RoiSection.tsx laborMathRows/
      // revenueMathRows). `view_dashboard` links the sourced input rows (orders,
      // 👎 rate, average check) to the relevant dashboard; `view_source` links
      // the product-constant rows (return rate, handling time, GM pay) to the
      // Help Center article. Each `*_aria` is a SUPERSET containing its visible
      // label verbatim (WCAG 2.5.3 Label in Name).
      view_dashboard: 'View dashboard',
      view_dashboard_aria: 'View dashboard: %{report}',
      view_source: 'View source',
      view_source_aria: 'View source: %{metric}',
      report_purchases: 'Purchases report',
      report_feedback: 'Order feedback report',
      report_check: 'Average check report',
      // Appended to every deep-link's accessible name — the links open in a new
      // tab (target=_blank), and the ExternalLink glyph is the visible cue.
      opens_new_tab: '(opens in a new tab)',
      // Floor label for the shared 👎 rate row: a positive rate too small to
      // render (percentageFormatter, one decimal, rounds it to "0%") prints
      // "< 0.1%" instead, so it never shows "× 0%" above a non-zero
      // dissatisfied-guest count.
      rate_below_floor: '< %{rate}',
    },
    cta: {
      title: 'Free to activate — you only pay when it works',
      activate: 'Activate RecoveryAI',
    },
    help: {
      title: 'Help',
      how_it_works: 'Learn how RecoveryAI works',
      faq: 'View Frequently Asked Questions',
    },
    // The promo GATE's error twin, not a section of the marketing page. When the
    // recovery-config GET fails hard (5xx / network) and nothing is cached, we
    // cannot tell an ACTIVE merchant from a never-configured one — so Landing
    // shows this recoverable surface instead of defaulting to the marketing page
    // and hiding a live console behind a transient failure.
    load_error: {
      message: "We couldn't load RecoveryAI.",
      retry: 'Try again',
    },
  },
  tabs: {
    overview: 'Overview',
    escalations: 'Escalations',
    agent_tuning: 'Agent Tuning',
    settings: 'Settings',
  },
  // Shown in place of the Agent Tuning / Settings tab bodies when RecoveryAI is
  // not active for the merchant (not onboarded, or operating_mode 'off').
  tab_locked: 'Enable RecoveryAI to view this tab.',
  // Page header status pill + location-count subtitle (AI-36). `status_active`
  // is the green ACTIVE pill label. In the subtitle, `count` is the TOTAL
  // location count (it drives the singular/plural form, per this file's
  // convention) and `active` is the count of active locations — an em-dash
  // placeholder until the backend exposes the field (coordinate PR-N1).
  header: {
    // Visually-hidden prefix so the ACTIVE pill is announced with context
    // ("RecoveryAI status: Active") rather than a bare "Active" (AI-36 review).
    status_label: 'RecoveryAI status:',
    status_active: 'Active',
    locations_one: 'Currently active at %{active} of %{count} location',
    locations_other: 'Currently active at %{active} of %{count} locations',
  },
  // Settings hub (AI-27): the 6-tab vertical layout under the console Settings
  // tab. NOTE: distinct from `tabs.settings` above (the console nav-tab label).
  settings: {
    tabs: {
      recovery_generosity: 'Recovery generosity',
      brand_voice: 'Brand voice',
      agent_introduction: 'Agent introduction',
      canned_responses: 'Canned responses',
      active_locations: 'Active locations',
      approved_rewards: 'Approved rewards',
      escalation_contacts: 'Escalation contacts',
      escalation_rules: 'Escalation rules',
      daily_budget: 'Daily budget',
      restricted_customers: 'Restricted customers',
      gaming_review: 'Suspected abuse review',
      setup_guide: 'Setup guide',
    },
    tablist_aria_label: 'RecoveryAI settings',
    // AI-33: pre-activation gate copy for the Escalation Rules pane. The
    // editable card only renders once `onboarding_completed` is true.
    escalation_rules_locked:
      'Escalation rules become available after you activate RecoveryAI.',
    // AI-33: surfaced when the config FETCH fails (so we don't show the
    // misleading "locked" copy) and when a PUT save fails (inline, non-blocking).
    escalation_rules_fetch_error:
      'We couldn’t load your escalation rules. Please refresh to try again.',
    escalation_rules_save_error:
      'Failed to save escalation rules. Please try again.',
    // AI-33: accessible name for the loading state (the shared Spinner renders
    // no role/aria text of its own).
    escalation_rules_loading: 'Loading escalation rules',
    // AI-121: explicit Save footer copy (replaces the prior debounced auto-save).
    escalation_rules_save: 'Save',
    escalation_rules_save_success: 'Escalation rules saved.',
    // AI-576: Daily budget pane copy — flat keys under settings, mirroring the
    // escalation_rules_* set. Label/desc/helper are reused from
    // recovery_ai.wizard.limits.daily_cap* via a second translator in the pane.
    daily_budget_locked:
      'Daily budget becomes available after you activate RecoveryAI.',
    daily_budget_fetch_error:
      'We couldn’t load your daily budget. Please refresh to try again.',
    daily_budget_save_error: 'Failed to save daily budget. Please try again.',
    daily_budget_loading: 'Loading daily budget',
    daily_budget_save: 'Save',
    daily_budget_save_success: 'Daily budget saved.',
    // Shown only when the current value is exactly $0 — a real, merchant-selectable
    // state (the AI auto-issues no paid reward and every costed case escalates).
    daily_budget_zero_notice:
      'Setting this to $0 means RecoveryAI will not automatically issue any paid reward at any location. Every case that would cost money is escalated to you. (Free goodwill actions are still allowed.)',
    // Read-only state when the stored value is the limitless sentinel. Per product
    // (Aaron, 2026-08-04) "unlimited" is the only budget v1 supports and there is
    // no self-serve / contact-support path, so the copy states the fact with no CTA.
    daily_budget_unlimited: 'Unlimited — no daily budget cap is applied.',
    // AI-576 (F1): read-only state when the stored cap EXCEEDS the FE-editable max
    // (CS/DB-set only — the merchant can never type it). Shown instead of an
    // editable field so a Save can't silently clamp a >$100k cap down to $100k. No
    // support CTA (there is no CS workflow for v1); the branch is purely defensive.
    daily_budget_above_max:
      'Your daily budget is above the $%{max} maximum shown here.',
    // AI-121 (R3): native confirm/beforeunload copy shown when the merchant tries
    // to leave a Settings pane with unsaved edits. Shared by both Settings panes
    // (Escalation rules + Brand voice) via useUnsavedChangesPrompt — pane-agnostic
    // wording so one key covers both.
    unsaved_changes_prompt:
      'You have unsaved changes. Are you sure you want to leave this page?',
    // Inline error surfaced when the Recovery generosity Save PUT fails (R12 /
    // AI-62 replaced the AI-29 auto-save-on-select with explicit Save). Mirrors
    // the wizard's generic `wizard.submit_error` — backend detail stays off-screen.
    recovery_generosity: {
      // Settings pane heading. Named "Recovery generosity" to match this pane's
      // tab label; the shared wizard step keeps its own "Recovery philosophy"
      // heading (recovery_ai.wizard.philosophy.title).
      title: 'Recovery generosity',
      // AI-917: explains where the "Recommended for you" badge comes from.
      // Settings-only — the Agent-Tuning modal (RecoveryConfigModals) omits this
      // line since it has no live assessment post-onboarding.
      recommendation_basis:
        'Recommendation is based on your onboarding assessment.',
      // Accessible name for the loading status region (WCAG 4.1.3) — mirrors the
      // sibling panes' loading copy.
      loading: 'Loading recovery generosity',
      save_error: 'Failed to save your selection. Please try again.',
      // Surfaced when the GET for the saved posture fails (AI-29 review) so the
      // pane never silently shows the 'generous' default as if it were the
      // merchant's real saved setting. Cards are blocked until reload.
      load_error:
        "We couldn't load your saved setting. Please refresh the page.",
      // AI-62 (R12): explicit Save footer copy (replaces the prior debounced
      // auto-save-on-select). Mirrors the brand_voice / escalation_rules footers.
      save: 'Save',
      save_success: 'Recovery generosity saved.',
    },
    // AI-249: read-only canned-responses pane. The GET returns the 5-message set
    // (greeting first) with safe fallbacks; `generated` marks a stored
    // brand-voiced row vs a default. No edit here — regeneration is the
    // brand-voice-save trigger (BE), not this pane.
    canned_responses: {
      title: 'Canned responses',
      // AI-932: the old copy promised these "regenerate automatically whenever you
      // update your brand voice" as an unconditional fact. Regeneration runs on an
      // async worker, so that sentence was false wherever the queue had no
      // consumer — and it made the pane read as confirmation that a rebuild had
      // happened when none had. Describe the trigger, then point at the button.
      description:
        'The messages your AI assistant uses, written in your brand voice. Saving a new brand voice rebuilds them in the background — or rebuild them now.',
      // Accessible name for the loading status region (WCAG 4.1.3).
      loading: 'Loading canned responses',
      // Fail-soft notice when the store is empty or unreachable (not yet
      // generated, or the endpoint is unshipped while this tab is dark-launched).
      empty: "Canned responses aren't available yet.",
      // Per-entry indicator: a stored per-merchant (brand-voiced) row vs a default.
      generated_badge: 'Brand voice',
      // AI-932: names the never-generated state explicitly. Plain "Default" read as
      // a normal steady state, so a merchant whose brand voice had never been
      // applied could not tell that from a successful rebuild.
      default_badge: 'Default — your brand voice hasn’t been applied yet',
      // AI-932: freshness was previously invisible — generated_at was fetched and
      // then discarded, so stale and just-rebuilt copy were pixel-identical.
      generated_at: 'Updated %{timestamp}',
      // Manual rebuild (AI-932). Works regardless of the async worker, so it is
      // both the merchant's remedy and the way to verify a brand-voice change
      // actually reached the copy.
      regenerate: 'Regenerate',
      regenerating: 'Regenerating…',
      regenerate_aria:
        'Regenerate canned responses from your current brand voice',
      regenerate_error:
        'We couldn’t regenerate your canned responses. Please try again.',
      regenerate_success: 'Your canned responses have been rebuilt.',
      types: {
        greeting: 'Opening greeting',
        conclusion: 'Closing message',
        escalation: 'Escalation message',
        gamification: 'Suspected abuse message',
        ai_down: 'When RecoveryAI is off',
      },
    },
    // Setup guide (flag: recovery-ai-setup-guide). Navigation-only re-entry to
    // the onboarding wizard — the tab writes nothing; changes persist only when
    // the merchant saves inside the wizard (the Locations stage's Save or
    // Finalize). Copy stresses the no-change guarantee so a returning merchant
    // re-opens without fear of clobbering live settings.
    setup_guide: {
      title: 'Re-open the setup guide',
      description:
        "Walk through the RecoveryAI setup steps again. Your live settings won't change unless you save.",
      // Visible button label.
      button: 'Re-open setup guide',
      // Accessible name — deliberately a SUPERSET that CONTAINS the visible label
      // verbatim (WCAG 2.5.3 Label in Name) while adding product context for
      // screen-reader users navigating a list of buttons.
      button_aria_label: 'Re-open setup guide for RecoveryAI',
    },
    // AI-31: Active Locations pane — all-vs-subset per-location enablement.
    // Counts (N/M) read from the metrics endpoint; %{count} drives the
    // singular/plural form per this file's convention (the call site picks
    // _one/_other by count — react-redux-i18n does not auto-pluralize).
    active_locations: {
      title: 'Active locations',
      // Grey supporting line under the heading (SettingsPaneChrome subtitle).
      subtitle: 'Choose which locations RecoveryAI is active at.',
      // Accessible name for the loading status region (WCAG 4.1.3).
      loading: 'Loading active locations',
      // Surfaced when a GET fails (so the misleading "locked" copy is never
      // shown for a load failure).
      fetch_error:
        'We couldn’t load your active locations. Please refresh to try again.',
      // Pre-activation gate copy — the controls only render once RecoveryAI
      // is activated (onboarding_completed).
      locked:
        'Active locations become available after you activate RecoveryAI.',
      // Radio group accessible name (WCAG 1.3.1) — the two options below.
      group_label: 'Which locations should RecoveryAI run at?',
      // %{count} = the merchant's TOTAL location count M — "All locations"
      // means all of them, not the enabled subset (an em-dash until metrics
      // load — never a literal 0).
      all_locations_one: 'All %{count} location',
      all_locations_other: 'All %{count} locations',
      // Green pill next to the All option (replaces the old "(recommended)"
      // text suffix) — compact-radio-layout pass (AI-901 follow-up).
      recommended_pill: 'Recommended',
      subset: 'A subset of locations',
      // LocationMultiSelect labels (the component is i18n-agnostic; the pane
      // resolves these and passes them in).
      select_locations: 'Select locations',
      locations_selected_one: '%{count} location selected',
      locations_selected_other: '%{count} locations selected',
      no_locations_available: 'No locations available to select yet.',
      // AI-1177: client-side text filter above the listbox (placeholder +
      // accessible name for the search input) and its empty-state sentence
      // when the query matches nothing (distinct from no_locations_available,
      // which covers there being no locations to pick from AT ALL).
      search_locations: 'Search locations',
      no_locations_match: 'No locations match your search.',
      // AI-1173: fallback display name for a location row whose configured
      // `name` is null/blank — shown in both the multi-select options and the
      // removable selected-location rows so neither ever renders empty.
      unnamed_location: 'Unnamed location',
      // AI-1173 follow-up: same fallback, but with the street address
      // appended when the Redux-ORM Location record for this row has one —
      // e.g. "Unnamed location (180 Redwood St)".
      unnamed_location_with_address: 'Unnamed location (%{address})',
      // Section heading above the removable selected-location rows.
      selected_locations_label: 'Selected locations',
      remove_location_aria_label: 'Remove %{name}',
      // Save footer + result banners (persists via the dedicated PUT
      // recovery/location_configs — the config PUT does not accept these).
      // `save` is still used by the onboarding wizard's explicit Save button;
      // the Settings pane (AI-900) autosaves and has no Save button of its own.
      save: 'Save',
      // AI-900: inline in-progress indicator the Settings pane shows next to a
      // small spinner while its debounced autosave commit is in flight.
      saving: 'Saving…',
      save_error: 'Failed to save your active locations. Please try again.',
      // Shown when the per-location selection persisted but only the secondary
      // new-location auto-enroll write failed — the generic save_error would
      // wrongly imply the selection was lost (it wasn’t).
      save_error_config_only:
        'Your locations are saved, but the new-location setting couldn’t be updated. Please try again.',
      // AI-900 (review follow-up): there is no Save button to retry with, so
      // this rides beside the failure banner and re-fires commit() directly.
      retry: 'Try again',
      save_success: 'Active locations saved.',
      // Tooltip on the disabled Save in subset mode when nothing has changed yet
      // (e.g. just switched from "All") — names the action that makes Save active.
      save_disabled_hint: 'Deselect at least one location to save.',
      // Empty-selection variant: an opt-in merchant hydrates into subset mode with
      // nothing selected, where "deselect" is backwards — they must SELECT one.
      save_disabled_hint_empty: 'Select at least one location to save.',
      // AI-901 pilot-vs-staff performance callout. Behind the dev-only flag
      // `recovery-ai-active-locations-performance-banner`. The numbers are
      // interpolated from recovery/activities/metrics; the banner renders
      // nothing until the backend supplies a complete, outperforming set (no
      // invented figures). Whole sentence is one key so translators keep the
      // numeric clauses in grammatical order. The 👎 is design-mandated copy.
      performance_banner_one:
        'At your %{count} pilot location, response rate (%{responseRate}) and response time (%{responseTime}) has resulted in %{pilotReturn} of 👎 guests returning within 30d compared to just %{staffReturn} at locations where your staff manages recovery.',
      performance_banner_other:
        'At your %{count} pilot locations, response rate (%{responseRate}) and response time (%{responseTime}) has resulted in %{pilotReturn} of 👎 guests returning within 30d compared to just %{staffReturn} at locations where your staff manages recovery.',
      // Response-time clause, pluralized separately and interpolated as
      // %{responseTime} above (react-redux-i18n does not auto-pluralize).
      performance_banner_minutes_one: '%{count} minute',
      performance_banner_minutes_other: '%{count} minutes',
    },
    // AI-30: Approved Rewards pane — published-rewards multi-select bound to
    // config.approved_reward_ids. The make-good ladder (AI-172/AI-247) now
    // renders the auto-provisioned make-good rewards read-only above the
    // multi-select; `ladder.*` copies that section.
    approved_rewards: {
      // Pane heading — matches the 'approved_rewards' tab label.
      title: 'Approved rewards',
      description:
        'RecoveryAI selects from the following offers to resolve customer issues based on your agent configuration. Every grantable reward presently configured in Thanx is available by default, alongside a recommended reward baseline RecoveryAI auto-creates.',
      card_title: 'Your grantable rewards',
      card_desc:
        'Pulled from the active reward templates you’ve set up in Thanx offer management.',
      // AI-62 (R12): explicit Save footer copy (replaces the prior debounced
      // auto-save-on-select). Mirrors the brand_voice / recovery_generosity
      // footers.
      save: 'Save',
      // Primary-button copy for the Approved-rewards surfaces (modal + pane).
      // Distinct from the generic config-modal 'Save' so only this flow reads
      // "Save changes" (the other agent-tuning modals keep "Save").
      save_changes: 'Save changes',
      save_success: 'Approved rewards saved.',
      // NOTE: the reward-picker strings (select_rewards, rewards_selected_*,
      // selected_rewards_label, remove_reward_aria_label, no_rewards_available,
      // create_reward_cta) live in the top-level `recovery_ai.reward_picker`
      // block, shared with the wizard via RewardMultiSelect (AI-91).
      // Accessible name for the loading state (the shared Spinner renders no
      // role/aria text of its own).
      loading: 'Loading approved rewards',
      fetch_error:
        'We couldn’t load your approved rewards. Please refresh to try again.',
      save_error: 'Failed to save your approved rewards. Please try again.',
      // Make-good ladder (AI-172/AI-247) — merchant-facing label is now
      // "Recommended reward baseline" (AI-508/AI-11 rename; internal name kept).
      // Renders the make-good reward templates Thanx AI auto-provisions for the
      // merchant read-only above the picker, each badged "AI recommended".
      // Rewards surface via GET recovery/available_rewards (ai_generated ===
      // true); the badge owns the "AI" label, so the section copy is just the
      // title/subtitle plus the show-more/less expander strings.
      ladder: {
        title: 'Recommended reward baseline',
        subtitle:
          'A set of cap-based dollar-off rewards RecoveryAI recommends to scale recovery to issue severity.',
        // %{count} needs singular/plural variants (CLAUDE.md i18n rule).
        view_more_one: 'View %{count} more reward',
        view_more_other: 'View %{count} more rewards',
        show_less: 'Show less',
        // Accessible name for the per-row decline "×" (AI-330). Distinct from the
        // picker's reward_picker.remove_reward_aria_label — Aaron's decline
        // semantics (don't create this offer) differ from removing a grantable.
        remove_reward_aria_label: 'Remove %{name}',
        // Accessible name for the same "×" on a materialized row (AI-1121) — the
        // offer is a live, already-granted program, so "disable" (restorable via
        // Regenerate, while the tier is still in the current ladder — nexus
        // #1640) reads more accurately than "remove".
        disable_reward_aria_label: 'Disable %{name}',
        // Immediate-write helper (AI-330/AI-1121, resolved): ladder declines
        // persist on click — unlike the staged grantable picker below — so this
        // sets the expectation. Shown only when at least one "×" is actually
        // live — i.e. not a read-only (CASL) viewer, whose ×'s all render
        // disabled.
        saves_automatically:
          'Changes to recommended rewards save automatically',
        // Regenerate control (AI-332): a section-level button at the BOTTOM of the
        // ladder (never per row), always available when curation is on — even
        // after the merchant has declined every reward — so the empty ladder is
        // never a dead end. Clicking opens a confirmation dialog (regenerate is
        // destructive of curation — it clears non-materialized declines, no
        // undo); confirming restores the full recommended set (declined tiers
        // return, newly-added tiers surface, and — nexus #1640 — a disabled
        // materialized row re-enables in place if its tier is still current).
        // This is the ONLY restore path by design: nexus #1640 also added a
        // per-row enable endpoint, but we deliberately don't wire it here — one
        // recovery mechanism for every row, not two.
        regenerate: 'Regenerate rewards',
        regenerate_confirm_title: 'Regenerate recommended rewards?',
        regenerate_confirm_body:
          'This restores the full recommended reward baseline — including any rewards you’ve declined or disabled — and adds any newly recommended tiers. Rewards still active in your program are never changed.',
        // Distinct from the trigger label ('regenerate') so the dialog's confirm
        // button is unambiguous — the trigger stays in the DOM behind the dialog.
        regenerate_confirm_cta: 'Regenerate',
        // Empty state when every make-good reward has been declined but the
        // snapshot still exists (distinct from the BE-absent case, which hides the
        // whole section). Pairs with the always-present Regenerate button below.
        all_declined:
          'You’ve declined every reward in the baseline. Regenerate to restore the recommended set.',
        // Empty state for a snapshot the BE returned as genuinely empty (a
        // successful fetch, zero offers) — distinct from all_declined (rows exist
        // but are hidden) and from the BE-unreachable case (section stays hidden).
        none_yet: 'No recommended rewards have been generated yet.',
        // First-generation label for the empty snapshot. "Regenerate" would be
        // wrong when there is nothing to regenerate from.
        generate: 'Generate rewards',
        // Inline failure for a TRANSIENT generate/regenerate write error — a 403
        // (the endpoint additionally requires admin), a 404 (FE/BE flag skew), or a
        // 5xx. Cause-neutral, and safe to retry. The terminal 409 has its own copy.
        regenerate_error:
          'We couldn’t update the recommended rewards. Please try again.',
        // The 409 the BE returns when a regenerate would change nothing — the
        // presented set already equals the recommended one (AI-858 follow-up,
        // reverses AI-856's grandfathered-only copy). NOT terminal: declining a
        // reward and retrying can still produce a real diff, so the copy must
        // stay cause-neutral rather than reading as a permanent lock.
        no_changes:
          'Your rewards already match the recommended set, so there’s nothing to regenerate.',
        // Inline failure for a decline/disable write (AI-1121 follow-up). Unlike
        // regenerate, decline has no non-terminal status to special-case, so
        // there's only ever this one cause-neutral retry copy.
        decline_error: 'We couldn’t update this reward. Please try again.',
      },
    },
    // AI-32: Escalation Contacts pane. `rationale.*` powers the static "Your AI
    // agent escalates when:" list (U1); copy pinned from the Figma frame
    // (Escalation contacts/RAI - Settings.png).
    escalation_contacts: {
      admin_grouping_unavailable:
        "We can't confirm who's an admin right now, so the ADMIN labels below may be incomplete. Recipient selection still works.",
      rationale: {
        heading: 'Your AI agent escalates when:',
        scope:
          'The problem is outside what it can resolve — complex, unusual, or beyond its tools.',
        suspicious:
          'The guest is behaving suspiciously or a complaint looks like possible misuse.',
        food_safety:
          'The situation is high-stakes — like a food safety concern — and needs a person.',
      },
      // U2: notify-location-managers toggle (persists the existing config flag
      // escalation_routing_notify_location_managers). ON relabels the recipient
      // field to the fallback copy; OFF to the all-escalations copy.
      notify_managers_label: 'Notify location managers',
      notify_managers_desc:
        'Location managers will receive escalation emails for their locations.',
      recipient_field: {
        fallback: 'Fallback recipient for locations without a location manager',
        all: 'Recipient for all escalations',
      },
      // U3: grouped recipient picker over existing merchant users (never
      // free-typed emails — AI-4/D2). Group headers + ADMIN badge + the
      // chip-row summary + the deep-link invite affordance.
      group_admins: 'ADMINS',
      group_others: 'ALL OTHER USERS',
      admin_badge: 'ADMIN',
      // %{count} pluralization via a native key/key_plural pair (react-redux-i18n
      // does NOT auto-pluralize _one/_other for raw t(key, { count })).
      recipients_selected: '%{count} recipient selected',
      recipients_selected_plural: '%{count} recipients selected',
      selected_recipients_label: 'Selected recipients',
      select_recipient_placeholder: 'Select a recipient',
      remove_recipient: 'Remove %{name}',
      invite_prompt: "Can't find the right person?",
      invite_cta: 'Invite someone',
      // sr-only suffix appended to the invite link ONLY where it opens a new tab
      // (the wizard, which would otherwise unmount mid-flow state on navigation).
      // The Settings pane navigates in-tab and omits it.
      invite_cta_opens_new_tab: '(opens in a new tab)',
      // U5: the data-fetching pane — heading, the additional-recipients section,
      // save/loading/error copy, and the demoted "unavailable" notice shown when
      // the contacts resource 404/403s (flag-off / no RBAC view / pre-deploy) —
      // an explicit state, NOT a silent no-op.
      title: 'Escalation contacts',
      subtitle: "Who we should notify when RecoveryAI can't resolve an issue?",
      additional_label: 'Additional recipients (optional)',
      additional_desc:
        "Forward a copy of every escalation to these inboxes. Useful for ops teams tracking customer issues across locations and organizations that use ticket based tools like Zendesk. They don't replace primary routing above.",
      save: 'Save',
      save_success: 'Escalation contacts saved.',
      save_error: 'Failed to save your escalation contacts. Please try again.',
      notify_save_error:
        'Failed to update the location-manager notification setting. Please try again.',
      loading: 'Loading escalation contacts',
      fetch_error:
        "We couldn't load your escalation contacts. Please refresh to try again.",
      unavailable_notice:
        "Escalation recipient settings aren't available right now. You can review who's listed, but saving is disabled until this loads.",
      // The notice above promises something to review, which is only true when a
      // previous load left data behind. With no saved set at all the pickers are
      // empty AND disabled, so this names that instead of implying an empty list
      // is the merchant's own configuration.
      unloadable_notice:
        "We couldn't load your current escalation recipients, so they can't be changed right now. Refresh the page to try again.",
      save_disabled_tooltip:
        'Saving is unavailable until escalation recipients load.',
      save_disabled_no_permission:
        'You need manage permissions to change escalation contacts.',
      save_disabled_saving: 'Saving your changes...',
    },
  },
  // The default "Overview" tab (AI-36) now embeds a Looker dashboard (AI-43
  // reversal): the data team owns the reported content via Looker, so the FE
  // carries only the tab heading below. (The `tabs.overview` label is separate.)
  overview: {
    billboard: {
      // Interim copy; final copy + the real illustration are MKT/Design-owned
      // (AI-128, design-blocked — Reno left). Eyebrow is plural-safe via the
      // native key/key_plural pair (react-redux-i18n picks the form from `count`
      // — do NOT use _one/_other, which this lib does not auto-resolve; see
      // context/knowledge/i18n-pluralization.md).
      eyebrow: 'RecoveryAI is off at your remaining %{count} location',
      eyebrow_plural: 'RecoveryAI is off at your remaining %{count} locations',
      cta: 'Turn on RecoveryAI at more locations',
    },
    // Embedded empty state for the `none` deployment stage: RecoveryAI is on at
    // no guest-facing store, so there is no dashboard data. No CTA — the
    // billboard above it is the call to action.
    no_guest_facing: {
      message: "RecoveryAI isn't on at any guest-facing locations yet.",
    },
    // LookerEmbedTab heading (AI-43 reversal — the Overview tab now embeds a
    // Looker dashboard via `recovery_ai.overview.looker`). Interim copy; confirm
    // against Figma in a copy pass.
    looker: {
      title: 'Performance',
    },
  },
  sandbox: {
    title: 'Conversation Sandbox',
    configure_first_onboarding:
      'Complete RecoveryAI onboarding before you can use the sandbox.',
    configure_first_rewards:
      'Approve at least one reward in the Approved rewards section below before you can use the sandbox.',
    configure_first_cta: 'Go to configuration',
    // Simulator-shell status copy (AI-95 review): the role="status" loading
    // label and the recoverable error region when the config/rewards GET fails.
    // Distinct from sandbox.config_modal.* (the modal shell) — these are the
    // in-tab / standalone / wizard simulator states.
    loading: 'Loading your settings…',
    // AI-185: no longer tells the merchant to "refresh" — the error state now
    // offers an in-place Retry (load_error_retry) that re-runs the failed GET.
    load_error: "We couldn't load your settings.",
    load_error_retry: 'Try again',
    // The VISIBLE placeholder. Kept short on purpose (D2): the compose box is 36px —
    // one 20px line — and the previous "Type a message as the customer..." wrapped to
    // two lines in the phone's 210px of usable width, which inflated the whole send
    // area by 17px. The Figma has no placeholder at all; this is the agreed middle
    // ground, so it MUST stay short enough to render on one line.
    chat_placeholder: 'Message as the customer…',
    // The compose box's accessible name, kept fuller than the visible placeholder so
    // the control still announces what it is for. Separate key rather than reusing
    // chat_placeholder, which the `terminal` state blanks on purpose.
    chat_input_label: 'Type a message as the customer',
    // Screen-reader-only announcement when the conversation reaches a terminal
    // state (reward granted or escalated). AI-583's design keeps the greyed box
    // as the only VISIBLE cue ("no visible message"), but a disabled textarea
    // drops from the tab order silently — this restores an accessible cue.
    conversation_ended: 'This conversation is complete.',
    // VISUALLY HIDDEN sender attribution for the customer's own messages. The Figma
    // draws no "You" over them — they are the one typing — and this renders sr-only so
    // that stays true to the pixel. It exists because the alternative was no
    // attribution at all: MessageBubble carries no role/aria, the two speakers are
    // separated only by a grey fill, and labels collapse per run — so on
    // [ai, ai, user, user] a screen reader inside role="log" got one label at the top
    // and no signal that the speaker ever changed (WCAG 1.3.1).
    //   context: NOT the old `you_label`, which was the VISIBLE label this replaced.
    //   Named for its role so nobody restores it to the visible branch by mistake.
    customer_label_sr: 'You',
    default_assistant_name: 'RecoveryAI',
    // ENG-971: the small pager chip above the phone (rendered uppercase). The
    // scenario NAME renders beneath it from `personas.*.name`, so the chip
    // carries only the position — replaces the AI-162 combined
    // "Scenario n of 3 : name" heading.
    scenario_chip: 'Scenario %{number}/%{total}',
    // The free phase's own title, standing where the persona name sits during the
    // guided scenarios. Its absence is what used to unmount the whole centre
    // header on the guided → free transition and jump the phone upward.
    free_phase_heading: 'Test your own',
    // ENG-978: the reset control moved out of the bottom button row into a text
    // link above the phone, replacing the free phase's "New conversation"
    // button. AI-1111 moved the standalone/agent_tuning shell onto this same
    // link (via SimulatorEngine's onReset/resetDisabled), so this is now the
    // only reset copy on any simulator surface.
    reset_conversation: 'Reset conversation',
    // The guided phase's last-resort customer turn (cannedUserMessages.ts). The
    // script anticipates two turns; a live agent can always ask a third question
    // the script has no answer for, and the scenario would otherwise stall on an
    // unanswered question in front of the merchant. This says "I have nothing to
    // add", which is the permission the agent needs to stop gathering and decide.
    //   context: deliberately ONE shared line rather than per-persona copy — it
    //   is a fallback for a question we did not anticipate, so it cannot be
    //   specific to the complaint without risking being wrong about it.
    //   context: bounded by MAX_CLOSING_TURNS. An agent that keeps asking is
    //   eventually left unanswered on purpose; see useCannedConversation.
    canned_closing:
      "That's everything I can tell you — can you help me sort this out?",
    // ENG-977: the assistant's opening message, shown in the phone BEFORE the
    // customer types anything. `greeting` interpolates the merchant's name;
    // `greeting_generic` is the fallback for when the merchant record (and with
    // it the name) has not resolved. The blank line is intentional — the bubble
    // renders with `white-space: pre-line` so this reads as two paragraphs.
    greeting:
      "Hi! I'm %{merchant}'s AI assistant.\n\nI'm sorry your experience didn't go well. Can you share a bit more and I will do my best to help!",
    greeting_generic:
      "Hi! I'm your AI assistant.\n\nI'm sorry your experience didn't go well. Can you share a bit more and I will do my best to help!",
    // Shown beside the spinner that REPLACES the conversation while a settings
    // save is being applied — visible copy, and the accessible name of that
    // role="status" region. Covers BOTH halves of the wait (the canned-response
    // rebuild and the conversation restart), so it no longer names the opening
    // message the way it did when only the greeting refreshed.
    settings_applying:
      'Applying your new settings — the preview will be ready in a moment.',
    ai_thinking: 'RecoveryAI is thinking…',
    // AI-166 quick-reply chips above the sandbox chat input. The LABELS are
    // pinned to the AI-166 ticket; the OPENERS are interim first-person customer
    // messages — FLAGGED for a product copy pass against Figma RAI Testing [2].png.
    quick_replies: {
      // Accessible name for the chip set (role="group") — announced to screen
      // readers so the chips read as suggested conversation starters.
      group_label: 'Suggested topics',
      items_label: 'Issues with items',
      items_opener:
        'Hi, I just got my order but one of the items I paid for is missing from the bag.',
      delivery_label: 'Problems with delivery',
      delivery_opener:
        "My delivery is much later than the time I was quoted and I still don't have my food.",
      quality_label: 'Food quality',
      quality_opener:
        "The food I received arrived cold and didn't taste the way it should.",
    },
    // The wizard Testing stage's static per-scenario "AI reasoning" checklist,
    // PLUS two strings reserved for the deferred AI-212 brain-fed panel.
    //
    //   context: `title` ('Agent reasoning') still belongs to that deferred panel
    //   (hooks/websocket/recoveryChatSource.ts:56-64) and is still unused on
    //   purpose — KEEP it: six assertions across four suites assert that exact
    //   string is ABSENT, and an absence lock only means something while the
    //   string exists. `empty` is no longer in that category — the Figma parity
    //   pass wired it up as the wizard card's empty state.
    //   Everything else is ReasoningChecklist's. Copy is transcribed
    //   character-for-character from the canonical reasoning document —
    //   scenario 1 from the onboarding mockups, scenarios 2-3 authored and
    //   approved by Matthew (2026-08-16). Do not reword without a new sign-off.
    reasoning: {
      title: 'Agent reasoning',
      // NOW RENDERED (it was dead for the deferred AI-212 panel): DecisionLogPanel's
      // wizard branch shows this under a centred glyph, replacing the longer
      // decision_log.wizard_empty. No trailing period — the Figma has none.
      empty: "Your agent's thinking will appear here",
      // Matches decision_log.wizard_title on purpose: the wizard's mint card is
      // called "AI reasoning" in BOTH phases (static checklist while guided,
      // live decision log once free), so the merchant never sees it rename.
      // NOTE for anyone diffing against 50665d29e^: that commit's
      // reasoning.wizard_title was 'What your agent did'. Same key, new copy.
      wizard_title: 'AI reasoning',
      // Figma: bold step title, a SPACED EM DASH, then regular-weight prose.
      // A locale value rather than a component constant per CLAUDE.md's
      // all-or-nothing i18n rule; the leading/trailing spaces are load-bearing
      // and are pinned byte-exactly by scenarioReasoning.test.ts.
      title_separator: ' — ',
      // Step TITLES. Only step 4 branches (make-good vs route-to-team); step 3
      // interpolates the merchant's LIVE posture through
      // decision_log.posture_values (D12) — the card claims "your <posture>
      // setting", so a baked label would be a lie.
      research: 'Research the customer & order',
      set_tone: 'Set the tone',
      applied_posture: 'Applied your %{posture} setting',
      chose_make_good: 'Chose the make-good',
      route_to_team: 'Routed to your team for review',
      tweak_brand_voice: 'Tweak brand voice',
      tweak_generosity: 'Tweak generosity',
      tweak_rewards: 'Tweak approved rewards',
      // Per-scenario step BODIES. Stored per scenario even where the text
      // repeats (set_tone is deliberately identical across all three — it
      // describes the merchant's brand voice, which does not vary by scenario),
      // so a later single-scenario edit stays a one-key change.
      //
      //   context: the `→` inside a step-4 body is a locale VALUE, not JSX —
      //   CLAUDE.md's no-raw-Unicode rule governs JSX, and shipped locale
      //   values already carry arrows (locales/segments.ts:27,
      //   locales/liveUpdates.ts:282). Do NOT "correct" the lowercase opening
      //   of the applied_posture bodies, the spaced em dashes, or the arrows.
      scenarios: {
        vip_missing_item: {
          research:
            'A Gold-tier regular — orders most weeks, high lifetime value. A relationship worth protecting. No history of serial complaints / gaming the system.',
          set_tone:
            'Acknowledged the problem warmly and took it seriously before doing anything else. Your brand voice.',
          applied_posture:
            'first complaint, low-impact, so it made things right with a slightly-better item rather than asking for proof.',
          // Updated for the current premise (personaScenarios.ts): one of two
          // $12.50 Burrito Bowls never arrived — the make-good replaces the
          // missing item itself.
          outcome:
            'Matched the fix to what was lost → a Free Burrito Bowl. From the rewards you approved.',
        },
        first_time_delivery: {
          research:
            "A brand-new customer — first order with you, so there's no history to weigh. A first impression worth protecting.",
          set_tone:
            'Acknowledged the problem warmly and took it seriously before doing anything else. Your brand voice.',
          // Updated for the current premise (personaScenarios.ts): a $3.49
          // side of fries missing from a $35.43 order — a minor miss, not a
          // "total failure" — so the posture matches the loss rather than
          // reaching for a bigger gesture.
          applied_posture:
            'a minor miss on a first order, so it matched the value of what went missing rather than reaching for a bigger gesture.',
          outcome:
            'Matched the fix to what was lost → a Free Order of Fries. From the rewards you approved.',
        },
        repeat_complainer: {
          research:
            'Fourth complaint in 30 days, all reporting missing items. A pattern worth a human look rather than another automatic reward.',
          set_tone:
            'Acknowledged the problem warmly and took it seriously before doing anything else. Your brand voice.',
          applied_posture:
            'the repeat pattern crossed the escalation rule you set, so it held off on granting anything.',
          outcome:
            'Flagged the pattern and handed the case to your team. No reward was granted.',
        },
      },
    },
    decision_log: {
      title: 'Decision Log',
      empty:
        'Decision log will appear here after the AI assesses a resolution.',
      // AI-204: the onboarding wizard variant titles this panel "AI reasoning".
      wizard_title: 'AI reasoning',
      // `wizard_empty` was removed: the Figma's empty state reads "Your agent's
      // thinking will appear here", which already existed as `sandbox.reasoning.empty`,
      // so the wizard branch renders that key and this one had no caller left.
      reward_name: 'Reward',
      reward_cost: 'Cost',
      severity: 'Severity',
      reasoning: 'Reasoning',
      posture: 'Generosity posture',
      escalation_type: 'Escalation type',
      escalation_reason: 'Reason',
      dry_run: 'Dry run',
      // The LIVE "AI reasoning" narrative (wizard free phase + Agent Tuning tab).
      // Same voice as the guided checklist in `reasoning.*` above — the agent is
      // "it", the merchant's settings are "your", past tense, no field labels —
      // but composed from the real terminal envelope rather than frozen copy.
      //
      //   context: these are NEW keys rather than a reuse of `reasoning.*`. That
      //   block is sign-off frozen; staticScopeGuard.test.tsx pins the live tab
      //   against the two things unique to the scripted card ("Research the
      //   customer & order" and the /^Tweak / buttons), which these must never add.
      //   context: there is deliberately no "Research the customer & order" beat.
      //   No customer research happens in the sandbox — thanx-ai's dossier renders
      //   "Not available yet." until nexus sends a v2 context (AI-750) — so the
      //   step would be a claim the payload cannot support.
      steps: {
        title_separator: ' — ',
        set_tone: 'Set the tone',
        // `custom_text` is derived from the PRESENCE of brand voice text, and the
        // usual way to produce it is typing into the Brand voice field — document
        // upload is one optional path to that text, not the mode. So this must not
        // name a document. The `default` branch is the Thanx voice, not theirs;
        // `brand_voice_values.default` already calls it "Thanx default".
        set_tone_custom:
          'Replied in the brand voice you wrote, acknowledging the problem before anything else.',
        set_tone_default:
          'Replied in the Thanx default voice, acknowledging the problem before anything else.',
        applied_posture: 'Applied your %{posture} setting',
        // The posture SIZES the make-good; it does not choose resolve-vs-escalate
        // (that is the Bouncer's, upstream). An earlier draft claimed the setting
        // was why the case stayed off the team's desk, which inverted outright on
        // Cautious — whose own blurb is "it keeps things tight".
        applied_posture_body:
          'so it sized the make-good to that level of generosity rather than to the value of the loss alone.',
        chose_make_good: 'Chose the make-good',
        chose_make_good_body_approved:
          '%{reward}. From the rewards you approved.',
        chose_make_good_body: '%{reward}.',
        route_to_team: 'Routed to your team for review',
        // One per SandboxEscalationType, plus the generic `fallback` for a value
        // the FE has not modelled yet. Lowercase openings continue the bold step
        // title through the em dash, matching the guided checklist.
        //
        //   context: the TITLE carries the routing, so no body restates it — an
        //   earlier draft closed seven of these on "so it handed the case to your
        //   team", under a title that already said so.
        //   context: `appraiser_error` / `appraiser_timeout` / `tool_error` are
        //   thanx-ai FAILING (provider error, blank key, the app-timeout dial, or any
        //   other raise inside the tool), not a judgement. Their copy says so on
        //   purpose — narrating an outage as a decision is how an incident gets read
        //   as normal behaviour during UAT.
        escalation_reasons: {
          appraiser_error:
            'it hit a problem on our side before it could weigh the case.',
          appraiser_timeout:
            'it ran out of time before it could weigh the case.',
          tool_error:
            'it hit an unexpected error partway through, so nothing was decided here.',
          budget_exceeded:
            "none of your approved rewards fit inside what was left of today's recovery budget.",
          cant_resolve:
            "this one needed a person's judgement rather than an automatic fix.",
          hallucinated_offer:
            "it picked a reward that wasn't available for this order.",
          invalid_proposal:
            "the fix it put together didn't come through cleanly.",
          missing_summary:
            "it couldn't pin down what went wrong from this conversation.",
          no_eligible_rewards:
            'none of your approved rewards were a fit for an order this size.',
          no_priced_items:
            'this conversation had no priced order to size a make-good against.',
          no_rewards_available:
            'your approved reward list was empty, so it had nothing to offer.',
          talker_hard_escalation:
            'the conversation raised a signal that always goes to a person.',
          fallback: "it couldn't settle this one on its own.",
        },
      },
      severity_values: {
        low: 'Low',
        medium: 'Medium',
        medium_high: 'Medium-high',
        high: 'High',
        critical: 'Critical',
      },
      posture_values: {
        frugal: 'Cautious',
        balanced: 'Balanced',
        generous: 'Generous',
        whatever_it_takes: 'Whatever it takes',
      },
      // `escalation_type_values` is gone with the labelled readout it fed: it
      // mirrored nexus's PERSISTED vocabulary, which the sandbox stream never
      // sends. That vocabulary was localized for Case Detail's Escalation pane
      // under `case_detail.escalation.types`, removed with the pane (AI-912);
      // the stream's tool-level types are explained in
      // `steps.escalation_reasons` above.
    },
    sandbox_prompt: {
      title: 'Sandbox prompt',
      description:
        'Baseline default instructions injected above brand voice. Dev-only, never used in production.',
      applies_next_conversation:
        "Applies on the next conversation. Click 'New conversation' to use the updated prompt.",
      placeholder: 'Default instructions the RecoveryAI uses in sandbox...',
      save: 'Save',
      saving: 'Saving…',
      expand: 'Expand sandbox prompt editor',
      collapse: 'Collapse sandbox prompt editor',
    },
    // `you_label` ('You') was removed with the bubble redesign: the Figma labels only
    // the agent's messages, so the customer's own bubbles carry no sender label and
    // nothing rendered this key any more.
    scenario_label: 'Scenario',
    detail_customer: 'Customer',
    detail_location: 'Location',
    detail_order_total: 'Order total',
    detail_delivery_method: 'Delivery method',
    detail_delivery: 'Delivery',
    detail_pickup: 'Pickup',
    detail_order_date: 'Order date',
    detail_items: 'Items ordered',
    // OrderDetailsCard (right rail, every simulator surface) — the
    // order-context card that reappeared beside "Agent settings" once
    // ScenarioDetailPanel stopped rendering in the wizard (AI-162/ENG-971
    // dropped its LEFT-column placement, not order visibility altogether).
    // Collapsed from a multi-row card to two lines inside a slim card
    // (product feedback: the original card ate too much vertical space; a
    // single sentence read as too compressed). `_delivery`/`_pickup` and
    // `_one`/`_other` are picked manually at the call site by
    // delivery_method/quantity, not auto-pluralized.
    order_details_label: 'Current order details for this AI demo',
    order_details_content_delivery: '%{items} for delivery',
    order_details_content_pickup: '%{items} for pickup',
    order_details_item_one: '%{name} (%{price})',
    // `%{price} each` — `price` is the UNIT price (buildItemSummary passes
    // item.price_cents, not price × quantity), so the bare "(%{price})" this
    // replaced read as ambiguous/wrong for "2 × Burrito Bowl ($12.50)" (looks
    // like the price of both, not one).
    order_details_item_other: '%{quantity} × %{name} (%{price} each)',
    send_message: 'Send message',
    scenario: {
      missing_item: 'Missing item',
      late_delivery: 'Super late delivery',
      food_quality: 'Food quality issue',
      wrong_item: 'Wrong item received',
      entire_order_wrong: 'Entire order wrong',
    },
    // Standalone (re-enterable) simulator shell chrome (AI-95). FE-4 re-homes
    // this into the agent_tuning tab; the F1 staged-save commit on Save and the
    // dirty-prompt on Exit are deferred to FE-4 / AI-173.
    standalone: {
      title: 'Testing sandbox',
      exit: 'Exit without saving',
      save_and_return: 'Save & return to RecoveryAI',
      not_configured:
        'Finish setting up RecoveryAI before testing here. Use Exit above to return to the console.',
      // App-version requirement for the RecoveryAI Experience, shown above the
      // phone simulator on the embedded agent_tuning tab. AI-1112 removed the
      // "ready to test" heading/subheading that used to sit above this notice.
      app_version_notice:
        'Please note that users must be using app version 60.0.0 and up in order to get the RecoveryAI Experience',
    },
    // Guided 3-persona playback copy (AI-42). The keys (name/descriptor) are
    // referenced from personaScenarios.ts relative to this namespace.
    // ENG-971: `name` is now the wizard's on-screen scenario title (rendered
    // under the "Scenario n of 3" chip), so each one names the persona AND the
    // problem — it is no longer a bare persona noun.
    //   context: `opener` + `follow_up` are the two customer turns the guided
    //   phase drives into the LIVE agent (cannedUserMessages.ts) — the scripted
    //   customer half of a live conversation. Load-bearing: the guided phase has
    //   no other source for what the customer says.
    //   context: every turn must stay coherent with that persona's OWN
    //   order_context (personaScenarios.ts) — the agent reads the order record
    //   before it replies, so naming an item that is not on the order produces a
    //   reply about food the merchant never sold. cannedUserMessages.test.ts
    //   pins this for scenario 1.
    //   context: and every `follow_up` must NAME the item, not gesture at it. A
    //   live agent asks a clarifying question when the customer is vague, and the
    //   script has no third turn to answer with — scenario 3 originally said only
    //   "missing items again" and the agent replied "Which item was missing — the
    //   burrito, chips, salsa, or the horchata?", dead-ending the scenario
    //   mid-conversation. Naming the item up front is what keeps these
    //   deterministic. Pinned per persona by ITEM_MENTION in the same test.
    personas: {
      vip_missing_item: {
        name: 'VIP customer missing an item',
        descriptor: 'A loyal regular reporting a missing item',
        opener:
          'I only got one of the bowls I ordered. It meant my friend and I had to split one bowl meaning we both basically only got half a meal.',
        follow_up:
          'Everything else was right — the chips and queso and the drink both showed up. It was just one of the two Burrito Bowls that never made it into the order, so my friend and I had to split what should have been two meals.',
      },
      first_time_delivery: {
        name: 'First-time customer missing an item',
        descriptor: 'A new customer whose first order arrived incomplete',
        opener:
          'This was my first order with you and my fries never made it into the bag. Not exactly the first impression I was hoping for.',
        follow_up:
          'Everything else showed up fine — the sandwich and the drink were both there. It was just the fries that were missing.',
      },
      repeat_complainer: {
        name: 'Frequent complainer with a wrong order',
        descriptor: 'Has complained several times in the past month',
        opener:
          'Once again my order is wrong. This keeps happening every single time I order from you.',
        follow_up:
          "The horchata was missing again — same as last time. I'd like this sorted out properly.",
      },
    },
    // ⚠ IMMUTABLE DEMO TRANSCRIPTS (R1/R4, decision D0). `msg_*` and
    // `reward_label` are copied CHARACTER-FOR-CHARACTER from the canonical
    // transcript document and pinned byte-for-byte by staticTranscripts.test.ts.
    // Do NOT paraphrase, re-punctuate, "fix" the em dashes, or swap the straight
    // apostrophes for typographic ones.
    //   context: rows 1-2 of every scenario are absent on purpose — they are the
    //   shipped `greeting`/`greeting_generic` pair above, which already
    //   interpolates the merchant name (D1). `msg_<n>` uses the canonical table's
    //   row number so the keys diff 1:1 against the source document.
    //   context: two intentional oddities, both verbatim from the source —
    //   first_time_delivery.msg_6 uses a plain HYPHEN, not an em dash; and
    //   vip_missing_item.reward_label is CAPS while its sibling is sentence case.
    //   The "20 POINTS" badge is NOT stored here: it is generated from a number
    //   by reward_card.points_plural and uppercased in CSS (S6).
    static_transcripts: {
      // Accessible name for the focusable, scrollable transcript region. In
      // scripted-demo mode the log gains a tabIndex (there is no other focusable
      // element inside the phone), so it needs a name that is not the internal
      // "Conversation Sandbox" jargon (D11b).
      region_label: 'Sample conversation',
      // NOTE: `input_disabled` ('Sample conversation. Typing is disabled.') was
      // removed 2026-08-18. The scripted demo no longer renders a compose box at
      // all (Aaron), so there is no disabled control left to caption — and unlike
      // `title` below, nothing asserts this string's ABSENCE, so keeping it would
      // just be an unreferenced key.
      vip_missing_item: {
        msg_3: "I ordered the burger combo but the avocado wasn't in the bag.",
        msg_4:
          'Got it, the avocado was missing from your combo. Was anything else off with the order, or just the avocado?',
        msg_5: 'No, everything else was there. Just the avocado.',
        msg_6:
          "I'm so sorry your order came out wrong tonight — that's really frustrating, and thank you for flagging it. You order with us all the time, and looking after regulars like you matters a lot to us, so I want to make this right.",
        msg_7:
          "I've added a Free Scoop of Avocado (valued at $5+) to your account for your next purchase. You can use it within the next 180 days on orders of $15 or more at participating locations.",
        reward_label: 'SCOOP OF AVOCADO',
        msg_9:
          "I've also shared this with the restaurant team so they can better handle situations like this in the future.",
      },
      first_time_delivery: {
        msg_3: 'I waited two hours and my order never showed up.',
        msg_4:
          "Oh no, I'm so sorry — two hours with no food is not okay. Have you tried reaching out to the driver, or checked around your delivery location to see if it was left somewhere?",
        msg_5:
          'Yeah, I checked. Nothing at the door. The app says it was delivered but nothing ever came.',
        msg_6:
          "That's frustrating. Sorry about that. To make it right, I've added $30 off your next order to your account - you can use it anytime at participating locations, no minimum purchase required.",
        reward_label: '$30 off your next order',
        msg_8:
          "I've also shared this with the restaurant team so they can better handle situations like this in the future.",
      },
      repeat_complainer: {
        msg_3: 'Order had a problem again.',
        msg_4:
          'Sorry to hear that. Can you tell me a bit more about what happened with this one?',
        msg_5: 'Missing items again. Same as last time.',
        msg_6:
          "Got it, thanks for letting me know. I've shared this with our team and they'll follow up with you directly.",
      },
    },
    // Guided playback navigation (AI-42): Previous / Next / Complete + the
    // accessible per-dot progress labels.
    playback: {
      previous: 'Previous',
      // ENG-973: this is the Testing-step guided-scenario advance button
      // specifically (PlaybackNav) — "Next scenario" reads clearer than the
      // generic "Next" for what advancing here actually does. Do not confuse
      // with the unrelated tour.pager.next / tour2.pager.next coach-mark CTAs.
      next: 'Next scenario',
      complete: 'Complete',
      step: 'Step %{number} of %{total}',
      step_current: 'Step %{number} of %{total}, current',
      step_done: 'Step %{number} of %{total}, completed',
    },
    // Read-only agent-settings summary (AI-95). No chevron/modal actions — the
    // interactive chevron→config-modal wiring is AI-167 / FE-4.
    agent_settings: {
      title: 'Agent settings',
      // AI-204: wizard-variant subtitle (Figma) — omitted on the standalone surface.
      description:
        'These three settings shape every reply in the simulator. Adjust any of them and test again.',
      brand_voice_label: 'Brand voice',
      posture_label: 'Generosity',
      // AI-204: the wizard variant labels the generosity row "Recovery generosity".
      posture_label_recovery: 'Recovery generosity',
      rewards_label: 'Approved rewards',
      brand_voice_values: {
        default: 'Thanx default',
        custom_text: 'Custom text',
      },
      // Native key / key_plural pair — t('rewards', { count }) auto-pluralizes
      // (react-i18nify picks the _plural form when count !== 1). Do NOT use
      // _one/_other here (the library does not resolve those from count).
      //   context: "grantable" per the Figma. It is load-bearing rather than
      //   decorative — the count is active ∩ approved (the set the agent can
      //   actually hand out), not the raw approved-reward count.
      rewards: '%{count} grantable reward',
      rewards_plural: '%{count} grantable rewards',
    },
    // Rich reward preview card (AI-164). Reads nexus #565's enriched reward
    // fields (image_url / value_cents / points) and degrades to a text row
    // when they are absent. Namespaced under sandbox.reward_card.* so it never
    // clobbers the #3691 sandbox-shell keys (loading/load_error/standalone.*).
    reward_card: {
      valued_at: 'Valued at %{amount}',
      // Native key / key_plural pair — same auto-pluralization caveat as
      // agent_settings.rewards above.
      points: '%{count} point',
      points_plural: '%{count} points',
      // Non-interactive preview of the consumer app's CTA, shown beneath the
      // in-chat reward card in the static demo transcripts. Rendered as text,
      // not a link/button — the demo phone is inert (R3, decision S7).
      view_reward: 'View your reward',
      // AI-1197: shared fallback name for a nameless reward (reward.label
      // null) in the chat transcript's variant="chat" card — used as the
      // image's alt when there's a product image, and as the visible label
      // text in the sparkle fallback when there isn't.
      reward_fallback_name: 'Your reward',
    },
  },
  landing: {
    hero_title: 'Automate how you resolve customer issues',
    hero_description:
      'RecoveryAI helps you automatically resolve customer issues and protect valuable relationships. It reviews customer complaints, evaluates the situation, and recommends the best resolution based on your policies.',
    cta_title: 'Activate RecoveryAI',
    cta_description: 'Set up RecoveryAI in just a few quick steps.',
    cta_button: 'Get started',
    roi_title: 'Estimated impact of RecoveryAI',
    roi_per_month: '/month',
    roi_subtitle:
      'Estimated from your monthly order volume and average order value.',
    roi_labor_savings: 'Labor savings',
    roi_labor_formula:
      'monthly orders x complaint rate x handling time x GM hourly pay',
    roi_recovered_revenue: 'Recovered revenue',
    roi_recovered_formula: 'recovery-to-revisit rate x average order value',
  },
  agent_introduction: {
    title: 'Agent introduction',
    description: 'How RecoveryAI opens every conversation with a guest.',
    name_label: 'Agent name',
    name_placeholder: 'e.g. Mel',
    thanx_default: 'Thanx default',
    switch_to_default: 'Switch back to Thanx default',
    rest_label: 'Rest of the message',
    rest_helper:
      'Written by RecoveryAI from your brand voice. To change it, update',
    rest_helper_link: 'Brand voice',
    rest_fallback: "I'm here to help with your recent order. What went wrong?",
    preview_eyebrow: 'What guests see first',
    preview_aria_label: 'First message the guest sees',
    intro_named: "Hi! I'm %{agent}, %{merchant}'s AI assistant.",
    intro_named_generic: "Hi! I'm %{agent}, an AI assistant.",
    intro_default: "Hi! I'm %{merchant}'s AI assistant.",
    intro_default_generic: "Hi! I'm an AI assistant.",
    name_error_too_long: 'Use %{max} characters or fewer.',
    name_error_control_characters:
      'Use letters, numbers, spaces and punctuation only. No line breaks or tabs.',
    name_error_rejected: 'This name was not accepted. Try a different one.',
    save: 'Save',
    unsaved_changes: 'Unsaved changes',
    save_success: 'Agent introduction saved',
    save_error: 'Failed to save agent introduction. Please try again.',
    fetch_error:
      'We couldn’t load your agent introduction. Please refresh to try again.',
  },
  // Brand-voice copy shared by the Settings pane (Settings/BrandVoicePane) and
  // the Agent-Tuning modal (components/AgentTuningConfigModals) via BrandVoiceBody.
  // Lives outside `wizard.*` / `settings.*` so copy edits for one surface never
  // require touching the other's namespace.
  brand_voice: {
    title: 'Brand voice',
    description:
      'Define how RecoveryAI should sound when responding to customers. Describe your tone, communication style, and any phrases to use or avoid.',
    default_text:
      'We speak in a warm and respectful tone. We acknowledge issues directly, take responsibility for circumstances under our control, and resolve customer problems quickly and definitively. Avoid corporate jargon and overly formal language. Keep responses clear, empathetic, and solution-oriented.',
    // Drop-area sub-line in the Generate-from-documents sub-view (W7). Dropped
    // ".doc": thanx-ai's FileIngestor has no OLE2 branch, so a .doc reads as
    // unreadable and silently returns "couldn't derive" (Q7/Q12). ".md"/".csv"
    // added (AI-1120) — both read as text, same as ".txt".
    upload_hint: 'Upload PDF, DOCX, TXT, MD, or CSV file',
    // Per-file chip-row rejection copy.
    upload_error_type: 'Upload a PDF, DOCX, TXT, MD, or CSV file.',
    upload_error_size: 'File is too large. The limit is 50 MB.',
    write_placeholder:
      'Describe how your brand should sound when communicating with customers, including tone, style, and anything to avoid (e.g. Friendly and empathetic, casual but professional. Avoid overly formal language).',
    save_error: 'Failed to save brand voice. Please try again.',
    fetch_error:
      'We couldn’t load your brand voice settings. Please refresh to try again.',
    // AI-121: explicit Save footer copy for the Settings pane.
    save: 'Save',
    save_success: 'Brand voice saved.',
    // --- W7 (FR-6): unified single-textarea + "Generate from documents" sub-flow.
    // Self-contained block; recoveryAi.ts is a four-way merge surface. ---
    generate_from_documents: 'Generate from documents',
    using_default_hint: "You're using Thanx default voice",
    // Replaces option_write as the textarea's accessible name.
    textarea_aria_label: 'Brand voice',
    // Brand-voice-only footer copy. The shared config_modal.save ('Save') serves
    // four modals and must not change; Figma's "Saves changes" is a typo (Q2).
    save_changes: 'Save changes',
    upload_intro:
      'ThanxAI reads your uploaded documents and writes the voice into your text field. You can edit it after.',
    // ModalContainer ariaLabel when the sub-view omits its title.
    upload_view_aria_label: 'Generate brand voice from documents',
    upload_dropzone_title: 'Upload Document(s)',
    generate: 'Generate',
    // Abort control shown beside "Analyzing…" on the Settings pane (which, unlike
    // the Agent-Tuning modal, has no footer Cancel to escape a stuck derive).
    cancel_generate: 'Cancel',
    // Per-chip remove-button accessible name.
    remove_file: 'Remove %{filename}',
    // Analyzing status row (state 4); %{files} is the Oxford-comma list. The
    // live region announces this text directly — no separate aria-label, which
    // would override and hide the filenames from AT.
    analyzing: 'Analyzing %{files}',
    // Shown when the document can't be read (no text layer, parse timeout, too long) —
    // the artefact is the problem, so point the merchant at a better one.
    generate_error_unreadable:
      "We couldn't read that document. It may be a scanned image, an unusual format, or too long. Try a text-based PDF, DOCX, TXT, MD, or CSV — or write your brand voice below.",
    // Shown when the document read fine but lacked enough of the brand's own writing to
    // capture a voice — point at richer source material, not a different format.
    generate_error_too_thin:
      "We read your document, but it didn't have enough of your brand's own writing to capture a voice. Try an About page, marketing copy, a menu with written descriptions, or a style guide — or write your brand voice below.",
    // Shown for an our-end fault (upload/derive: presign, S3, network, 5xx, watchdog, or a
    // brain our-fault reason) — the document is fine, so don't tell the merchant to swap files.
    generate_error_retryable:
      'Something went wrong on our end. Please try again in a moment — if it keeps happening, let our support team know.',
    // i18n-safe list join — no comma / "and" literal in a .tsx file.
    list_join_two: '%{a} and %{b}',
    list_join_last: '%{head}, and %{last}',
    list_join_pair: '%{head}, %{next}',
  },
  // R12 (AI-168): generic footer copy for ConfigModalHost — the shared modal
  // shell that hosts any config-pane body over the (dimmed) simulator. Lives
  // here (not under brand_voice) because the host is reused by every config
  // modal (Brand voice / Recovery generosity / Approved rewards).
  config_modal: {
    cancel: 'Cancel',
    save: 'Save',
    save_and_test: 'Save and test',
    // Inline banner when the staged commit PUT fails — the modal stays open with
    // the edits intact so the merchant can retry or Cancel.
    save_error: 'Failed to save. Please try again.',
    // Status label while the config GET is in flight — the modal shows a spinner
    // instead of the editing body so the merchant never edits/saves defaults
    // before their real config loads.
    loading: 'Loading your settings…',
    // Shown when the config GET fails or returns no payload — the body is
    // withheld so a broken load can't be mistaken for (or saved over) real config.
    load_error: "We couldn't load your settings. Please close and try again.",
    // Post-save confirmation on the agent-tuning page after the modal closes
    // (PR#6). %{setting} is the saved row's title, mirroring the Settings panes'
    // per-setting copy ("Brand voice saved." / "Recovery generosity saved.").
    save_success: '%{setting} saved.',
    // Restart-aware variants, used on the surfaces where a successful save also
    // reloads the simulator conversation so the phone reflects the new settings from
    // a blank slate. Simple present ("restarts") rather than past, because the reset
    // is deferred behind any reply still in flight — so it may not have happened yet
    // when the banner appears, and "restarted" would be a lie in that window.
    //   context: only rendered when the save actually PUT something AND the surface
    //   restarts. A banner claiming a restart that did not happen is worse than no
    //   banner — see AgentTuningConfigModals' didCommitRef.
    save_success_restarted:
      '%{setting} saved — the test conversation restarts with your new settings.',
    // Post-save confirmation on the "Save and test" path. The agent simulator
    // (StandaloneSimulator) is on this same agent_tuning tab, above the config
    // rows, so rather than a no-op navigation we scroll it into view and point
    // the merchant at it — the click has visible feedback (AI-173 review
    // follow-up).
    save_success_test: '%{setting} saved — try it in the sandbox above.',
    save_success_test_restarted:
      '%{setting} saved — the test conversation restarts with your new settings. Try it in the sandbox above.',
    // AI-64: label for the collapsed disclosure that reveals the advanced
    // Escalation Rules row (kept out of the primary rows; has a sensible default).
    advanced: 'Advanced',
  },
  // Reward-picker copy shared by the Settings pane (Settings/ApprovedRewardsPane)
  // and the wizard step (Wizard/steps/limits/ApprovedRewardsCard) via the shared
  // RewardMultiSelect component + its buildRewardMultiSelectLabels helper. Lives
  // outside `settings.*` / `wizard.*` (mirroring `brand_voice` above) so there is
  // ONE source for these strings — the two surfaces can never drift again (AI-91).
  reward_picker: {
    select_rewards: 'Select rewards',
    // %{count} needs singular/plural variants (CLAUDE.md i18n rule). react-i18nify
    // does NOT auto-pluralize _one/_other — the call site picks the form by count.
    rewards_selected_one: '%{count} reward selected',
    rewards_selected_other: '%{count} rewards selected',
    selected_rewards_label: 'Selected rewards',
    remove_reward_aria_label: 'Remove %{name}',
    // Stands in for %{name} when a reward has no name at all — a legacy redeem
    // template whose name predates the settings→column migration
    // (AvailableReward.label is nullable). Without it the remove button's only
    // accessible name would be a bare "Remove ".
    unnamed_reward: 'this reward',
    no_rewards_available: 'No rewards available to select yet.',
    create_reward_cta: 'Create a reward',
    // Selected-chip collapse (Figma parity): show the first few, then a
    // "View N more" / "Show less" toggle — same %{count} plural rule as above.
    view_more_one: 'View %{count} more reward',
    view_more_other: 'View %{count} more rewards',
    show_less: 'Show less',
  },
  // Guest-value suffix ("$4.50 value") shown next to a reward's amount on
  // every reward-approval surface. Top-level (mirroring `reward_picker` /
  // `brand_voice` above) because it is shared by TWO components in
  // different i18n namespaces — the picker (RewardMultiSelect, via
  // RewardMultiSelect/labels.ts, itself bound to `recovery_ai.reward_picker`)
  // and the make-good ladder (`recovery_ai.settings.approved_rewards.ladder`)
  // — so there is ONE copy source they can never drift apart on. Both read it
  // through the shared `rewardValue.ts` module (formatRewardValue), never
  // directly.
  //
  // The number itself is deliberately GUEST VALUE, never COGS: nexus's own
  // build_consumer_context.rb (services/merchant-api/app/interactors/recovery/
  // conversations/build_consumer_context.rb:337) states it plainly —
  // "value_cents is the full up_to_cents: 'up to $X off' IS the value cap the
  // guest sees". COGS (`cost_cents`) does not render on these surfaces at all.
  reward_value: {
    // %{amount} is an already-formatted currency string (currencyFromCents),
    // not a count — no _one/_other plural handling needed.
    suffix: '%{amount} value',
  },
  // Cross-surface AI badge label — "AI recommended" (AI-243 / FR-54; the copy
  // was updated from the original "Created by Thanx AI" sparkle label). Shared
  // by AiGeneratedBadge. `created` marks a reward Thanx AI authored (the
  // make-good offers, gated on AvailableReward.ai_generated); `resolved` keeps
  // the ResolvedByCell "who closed the case" pill copy so consolidating the two
  // hand-rolled pills into the badge is copy-preserving.
  ai_generated_badge: {
    created: 'AI recommended',
    resolved: 'THANX AI',
  },
  // Scope explainer for the escalation notification setting, shared by the
  // Settings escalation-contacts pane and the wizard escalations step (via
  // EscalationNotificationScopeNote). Top-level, outside `settings.*` /
  // `wizard.*`, so both surfaces read ONE copy source and the wording can't
  // drift between them.
  escalation_notification_scope: {
    all_locations:
      'Notification settings apply to every location, whether or not it uses RecoveryAI.',
    ai_handled:
      "Where RecoveryAI is on, you're only notified about feedback it doesn't handle automatically.",
  },
  wizard: {
    stepper: {
      // R12 4-stage labels (Train / Testing / Locations / Escalations).
      train_your_agent: 'Train your agent',
      testing: 'Testing',
      locations: 'Locations',
      escalations: 'Escalations',
      // Legacy labels kept for any pane that still reads them; the R12 stepper
      // references only the four keys above.
      ai_identity: 'AI Identity',
      assessment: 'Assessment',
      recovery_philosophy: 'Recovery Philosophy',
      brand_voice: 'Brand Voice',
      limits: 'Limits & Guardrails',
      // DATA-5325 T2: escalation-routing split out of Limits; activation added last
      escalation_routing: 'Escalation Routing',
      activation: 'Activation',
    },
    testing: {
      // R12 Testing stage (pre-activation simulator embed — AI-160). The Figma
      // subtitle's second sentence was held back while the Agent-settings panel was
      // deferred to AI-167; that panel has landed (it is the settingsCard the wizard
      // now mounts), so the full sentence is restored and is byte-identical to
      // sandbox.standalone.subheading — the two surfaces mirror each other.
      title: 'Your AI agent is ready to test',
      // Count-free deliberately (not `%{count} scenarios`): scenario 3
      // (repeat_complainer) is hidden from the wizard but not deleted
      // (ACTIVE_PERSONA_SCENARIOS in personaScenarios.ts), and
      // react-redux-i18n does not auto-pluralize `%{count}` (see
      // context/knowledge/i18n-pluralization.md) — an interpolated count would
      // need a `_one`/`_other` pair kept in sync with that array by hand. A
      // count-free sentence can never drift when a scenario is added/removed.
      description:
        "Preview how your agent handles the scenarios below. If a reply doesn't feel right, adjust the settings on the right to change the responses.",
      // App-version requirement for the RecoveryAI Experience, shown above the
      // phone simulator — byte-identical to sandbox.standalone.app_version_notice.
      app_version_notice:
        'Please note that users must be using app version 60.0.0 and up in order to get the RecoveryAI Experience',
    },
    // AI-162 guided coach-mark / spotlight tours over the Testing-stage phone
    // simulator. Copy transcribed from the Figma wizard-testing frames. `tour` is
    // the 5-step FIRST tour (guided playback); `tour2` is the 3-step SECOND tour
    // (the "Test your own" free phase). Dark card, teal heading/CTA, no Back/Skip.
    tour: {
      dialog_label: 'RecoveryAI simulator tour',
      steps: {
        simulator: {
          title: 'This is your agent simulator',
          body: 'Watch how your agent handles a real situation — and use the controls to shape how it responds.',
        },
        controls: {
          title: 'These are your controls',
          body: "Brand voice, generosity, and approved rewards shape every reply. Change any of them and the agent responds differently — you'll see how as we go.",
        },
        customer: {
          title: 'What your customer sees',
          body: 'Each scenario uses a different consumer profile - a VIP customer, a 1st time guest. Evaluate how your agent responds to each.',
        },
        settings: {
          title: 'Your settings drive every decision',
          body: 'In this simulator you can see how your agent makes decisions using the settings you control.',
        },
        playback: {
          title: 'Test each scenario',
          body: "Once you're satisfied with how your agent handles a given scenario use the navigation to see the next scenario.",
        },
      },
      pager: {
        dot: 'Go to step %{number} of %{total}',
        next: 'Next',
        done: 'Done',
        okay_show_me: 'Okay, show me',
        got_it: 'Got it',
      },
    },
    tour2: {
      dialog_label: 'Test your own scenarios tour',
      steps: {
        seen: {
          title: "Nice work — you've seen your agent in action",
          body: "You've watched it handle both situations. Now you can put it to the test yourself, or move on when you're ready.",
        },
        test_own: {
          title: 'Test it with your own scenarios',
          body: 'Bring an order issue to your agent — type in the chat like a customer would, and see how it responds.',
        },
        ready: {
          title: 'Ready when you are',
          body: "Once you've fine-tuned everything and you're confident in how your agent handles things, click Continue to move on to the next step.",
        },
      },
      pager: {
        dot: 'Go to step %{number} of %{total}',
        next: 'Next',
        done: 'Done',
        got_it: 'Got it',
      },
    },
    locations: {
      // AI-879: copy matches the approved Figma mock for the wizard Locations
      // stage. The 👎 emoji is part of the approved copy (same precedent as the
      // pilot performance banner above).
      title: 'Select locations to activate RecoveryAI',
      description:
        'RecoveryAI will automatically engage guests who leave a 👎 rating on a digital order. You can then compare their performance against your other locations and see incremental ROI.',
      // WCAG 4.1.3: sr-only reason for the disabled Continue when the merchant
      // picks "a subset of locations" but selects none — advancing would leave
      // RecoveryAI running at no location at all.
      continue_empty_subset_reason:
        'Select at least one location, or choose all locations, to continue.',
      // Visible + keyboard-accessible hover tooltip on the disabled Continue
      // (via TooltipWhenDisabled), shown alongside the sr-only reasons above —
      // shorter copy for an at-a-glance hover, one per blocking reason.
      continue_tooltip_empty_subset: 'Select at least one location.',
      // Confirm dialog shown when Back or Cancel would leave the Locations step
      // with an unsaved per-location edit — those paths re-derive from saved
      // truth on re-entry, so leaving silently discards the draft (AI-996).
      leave_confirm_title: 'Discard unsaved changes?',
      leave_confirm_body:
        'Your location changes on this step have not been saved yet. Leaving now will discard them.',
      leave_confirm_cta: 'Discard and leave',
    },
    quick_start: {
      wordmark: 'ThanxAI',
      headline:
        "In just a few clicks, you'll have a recovery agent that resolves complaints the way you would",
      subtitle:
        "Answer a few quick questions about how you handle unhappy guests. ThanxAI will turn your instincts into a working agent — then you'll test and fine-tune it before anything goes live.",
      minutes_note: 'Takes about 5 minutes',
      get_started: 'Get started',
      step1_title: '1. Share your recovery philosophy',
      step1_description: 'A few questions for context',
      step2_title: '2. Train your agent',
      step2_description: 'Tune response behavior',
      step3_title: '3. Settings and review',
      step3_description: 'Select locations and launch',
    },
    train: {
      title: 'Train your agent — how do you manage customer issues today?',
      description:
        "Tell us how you handle unhappy guests today. ThanxAI will use your instincts to recommend settings that manage the agent's voice, generosity, and judgment for your review.",
      info_banner:
        "These questions help us recommend a recovery philosophy. They don't directly control how your recovery agent responds.",
    },
    processing: {
      logo_alt: 'ThanxAI',
      headline: 'Configuring your RecoveryAI',
      subtitle:
        'ThanxAI is translating your answers into a recovery philosophy tailored to your brand.',
      checklist_label: 'Configuration progress',
      // WCAG 1.3.1: sr-only per-row state, since the spinner/tick icons are
      // aria-hidden and an active row shares its label with the queued ones.
      step_state: {
        done: 'Completed',
        active: 'In progress',
        pending: 'Not started',
      },
      // The checklist ticks over item by item, so each line needs both tenses:
      // `active` reads while the item is spinning (and for the items still
      // queued behind it), `done` once it is ticked green.
      steps: {
        trust: {
          active: 'Calibrating trust posture from your answers',
          done: 'Calibrated trust posture from your answers',
        },
        generosity: {
          active: 'Setting generosity stance to match your style',
          done: 'Set generosity stance to match your style',
        },
        brand_voice: {
          active: 'Drafting your brand voice',
          done: 'Drafted your brand voice',
        },
        rewards: {
          active: 'Selecting rewards from your catalog',
          done: 'Selected rewards from your catalog',
        },
        escalation: {
          active: 'Tuning escalation rules',
          done: 'Tuned escalation rules',
        },
      },
      // AI-323 (a11y): sr-only status announcements for the auto-advancing
      // interstitial. Progress-only phrasing (does NOT restate the visible
      // `headline`, which focus-on-mount already lands the screen reader on) to
      // avoid a double-announce on entry.
      status_calibrating:
        'Calibration in progress. This will only take a moment.',
      status_complete:
        'Configuration complete. Continuing to the testing step.',
      // AI-323 (WCAG 4.1.3): programmatic blocking reason for the disabled
      // Continue during calibration, referenced via aria-describedby.
      continue_disabled_reason: 'Continue unlocks once configuration finishes.',
    },
    escalations: {
      finalize: 'Finalize',
      // AI-580: a re-entry whose config GET failed never loaded the merchant's
      // saved settings, so Finalize is blocked to avoid re-PUTing defaults over
      // live config. Tell them to retry rather than dead-ending on a disabled button.
      finalize_config_error_title: "We couldn't load your current settings",
      finalize_config_error_body:
        "Refresh the page and try again before finalizing, so your saved RecoveryAI settings aren't overwritten.",
      // ENG-984 (WCAG 4.1.3): sr-only reason for the disabled Finalize, in the
      // settled case where nothing is chosen yet. D2 gates on a reachable human
      // — a primary escalation recipient OR notify-location-managers — so it
      // names BOTH ways out (see useActivationCompleteness). The visible "finish
      // these steps" checklist was removed from this step; this is the only
      // remaining (non-visual) explanation of the disabled state.
      finalize_incomplete_reason:
        'Finalize unlocks once you choose an escalation recipient, or turn on notifications for location managers.',
      // The rest of the blocking-reason set, one string per state the recipient
      // gate can be in. They are distinct reasons, not variations on the one
      // above: a merchant who is mid-save or whose recipients failed to load has
      // nothing to fix in the form, so telling them to pick a recipient would be
      // wrong. "Unavailable" keeps the location-managers escape hatch in view,
      // since that leg alone satisfies the gate.
      finalize_checking_recipients: 'Checking your escalation recipients…',
      finalize_saving_recipients:
        'Finalize unlocks once your escalation recipients finish saving.',
      finalize_save_failed:
        "We couldn't save your escalation recipients. Try again above before finalizing.",
      finalize_recipients_unavailable:
        "To finalize, refresh the page or turn on notifications for location managers — we couldn't load your escalation recipients.",
      // Retry control for a failed recipient save, offered inline beside the
      // failure so the merchant never has to leave the step to recover.
      retry_save: 'Try again',
      // AI-883: re-added after being pulled as a stub (L5 — it pointed at a
      // help article that was never written). Now points at a real, published
      // article (see HELP_LINK_URL in EscalationRouting/index.tsx).
      help_title: 'Help',
      help_link: 'How to configure a location manager',
      help_link_aria: 'Help article: how to configure a location manager',
    },
    ai_identity: {
      title: 'AI Identity',
      description:
        'Define how your assistant presents itself to customers, including its name and whether it identifies as AI.',
      name_label: 'Assistant name',
      name_placeholder: 'Enter a name for your assistant',
      name_helper:
        'This is the name customers will see when interacting with your assistant.',
      mode_label: 'How should your assistant introduce itself?',
      ai_identified: 'Identify as an AI assistant',
      ai_identified_desc: 'Be transparent that responses are powered by AI.',
      ai_identified_example:
        '\u201CHi, I\u2019m %{name}, PizzaCo\u2019s AI assistant.\u201D',
      brand_assistant: 'Use a brand assistant (no AI mention)',
      brand_assistant_desc:
        'Present your assistant as part of your brand experience.',
      brand_assistant_example: '\u201CHi, I\u2019m %{name} from PizzaCo.\u201D',
      custom: 'Custom introduction',
      custom_desc: 'Write your own greeting to match your brand voice.',
      custom_placeholder: 'Enter your custom introduction...',
      default_name: 'your assistant',
    },
    assessment: {
      title_bold:
        'Please take a moment to quickly assess your recovery philosophy',
      info_text:
        "These questions help us recommend a recovery philosophy. They don't directly control how your recovery agent responds.",
      q1_badge: 'Trust',
      q1_question:
        "A first-time customer reports their order arrived wrong. What's your instinct?",
      q1_option_1: 'Ask for details or proof before offering anything',
      q1_option_2: 'Give the benefit of the doubt with a small gesture',
      q1_option_3: 'Trust them and make it right',
      q1_option_4:
        'Trust them and go above and beyond — first impressions matter most',
      q2_badge: 'Generosity',
      q2_question:
        'A regular customer says their $40 delivery arrived cold and was missing an item. What feels right?',
      q2_option_1: 'Offer a discount on their next order',
      q2_option_2: 'Credit them for the missing item',
      q2_option_3: 'Comp the full order',
      q2_option_4: 'Comp the order and add something extra for the trouble',
      q3_badge: 'Loss Aversion',
      q3_question: 'What keeps you up at night?',
      q3_option_1: 'Giving away too much to people gaming the system',
      q3_option_2: 'Spending more than necessary on resolutions',
      q3_option_3: 'A frustrated customer not coming back',
      q3_option_4: 'A single bad experience undoing months of loyalty',
    },
    cancel: 'Cancel',
    continue: 'Continue',
    back: 'Back',
    back_to_activation: 'Back to activation',
    submit_error: 'Failed to save configuration. Please try again.',
    philosophy: {
      title: 'Recovery philosophy',
      description:
        'Set the principles RecoveryAI should follow when resolving customer issues. This guides how it responds and what it prioritizes.',
      card_label: 'Your philosophy',
      slider_aria_label: 'Recovery philosophy posture',
      // Blue probabilistic info banner above the stance cards (AI-29 / R10
      // Figma). Figma copy had "it's"; corrected to the possessive "its".
      info_banner:
        "RecoveryAI's behavior is probabilistic — these options shape its decision-making. The agent will lean toward these actions, but each response is shaped by the specific situation.",
      // Column headings for the expanded (selected) stance card (AI-1179
      // decided copy, 8/27).
      more_likely_to: 'More likely to:',
      less_likely_to: 'Less likely to:',
      // Stance display labels. The `frugal` posture SHOWS as "Cautious" (AI-240);
      // the wire/enum value stays `frugal` (D2) — key≠label, same pattern used
      // elsewhere in this file.
      frugal: 'Cautious',
      // AI-1179 decided copy (8/27) — verbatim, do not edit for grammar.
      frugal_desc:
        'RecoveryAI is more likely to make clear misses right and less likely to go any further than that. Quality and timing issues tend to get a small gesture, and guest history rarely moves the needle.',
      balanced: 'Balanced',
      balanced_desc:
        'RecoveryAI is more likely to match the value of what went wrong than to exceed it. Loyal regulars and first-time guests may see the recovery nudged up slightly, but it stays anchored to the loss.',
      generous: 'Generous',
      generous_desc:
        'RecoveryAI is more likely to make things whole and then some, especially for your best and newest guests.',
      whatever_it_takes: 'Whatever It Takes',
      whatever_it_takes_desc:
        'RecoveryAI is more likely to exceed the loss than to match it, on nearly every recovery. There is still a ceiling, it is just set higher than any other stance. An underwhelming outcome is unlikely for any guest.',
      recommended: 'Recommended',
      recommended_for_you: 'Recommended for you',
      // Per-stance "More likely to:" / "Less likely to:" bullets (AI-1179
      // decided copy, 8/27). Bullet counts vary per posture — see
      // `POSTURE_BULLET_COUNTS` in ./stances.ts, the single source of truth
      // for which keys exist per posture. Never enumerate a key here that
      // isn't listed there (and vice versa).
      frugal_more_1: 'Make it right when something is clearly missing or wrong',
      frugal_more_2:
        'Offer a small gesture on quality or timing issues rather than a full replacement',
      frugal_less_1: 'Go beyond the value of what was actually lost',
      frugal_less_2: 'Factor in guest history when deciding how far to go',
      balanced_more_1:
        'Match the value of what went wrong and stay proportional',
      balanced_more_2: 'Make missing or wrong items whole',
      balanced_more_3:
        'Offer a meaningful gesture on quality or timing complaints rather than just empathy',
      balanced_more_4:
        'Nudge recovery up slightly for loyal regulars and first-time guests',
      balanced_less_1: 'Exceed the value of the loss',
      balanced_less_2: 'Treat every guest the same regardless of history',
      generous_more_1:
        'Make missing or wrong items whole and then some, especially for high-value or first-time guests',
      generous_more_2:
        'Offer an equivalent make-good on quality and timing complaints rather than a partial gesture',
      generous_more_3: 'Factor guest history into how generous the response is',
      generous_less_1:
        'Offer just a partial gesture on quality or timing complaints',
      generous_less_2:
        'Keep recovery strictly anchored to the value of the loss without adding something on top',
      whatever_it_takes_more_1:
        'Exceed the value of the loss on missing or wrong items',
      whatever_it_takes_more_2:
        'Offer an equivalent make-good or more on quality and timing complaints',
      whatever_it_takes_more_3:
        'Go further for guests with strong history, but keep the floor high for everyone',
      whatever_it_takes_less_1:
        'Offer less than the full value of what was lost',
      whatever_it_takes_less_2:
        'Let a recovery feel underwhelming regardless of guest history',
    },
    limits: {
      title: 'Limits & guardrails',
      description:
        'Define financial safeguards and approval thresholds for automated resolutions.',
      daily_cap: 'Daily budget per location',
      daily_cap_desc:
        'Maximum credits RecoveryAI can issue per location each day.',
      daily_cap_helper:
        'Once a location reaches its daily budget, RecoveryAI will stop automatically resolving issues. New cases will be escalated and sent to the merchant for review via email.',
      daily_cap_tip:
        'Tip: most merchants set this based on their busiest locations and highest daily complaint volume.',
      approved_recovery_rewards: 'Approved recovery rewards',
      approved_rewards_desc:
        'Select which rewards RecoveryAI can automatically issue when resolving customer issues.',
      // NOTE: the reward-picker strings (select_rewards, rewards_selected_*,
      // selected_rewards_label, remove_reward_aria_label, no_rewards_available,
      // create_reward_cta) live in the top-level `recovery_ai.reward_picker`
      // block, shared with the Settings pane via RewardMultiSelect (AI-91).
      escalation_rules: 'Escalation rules',
      escalation_rules_desc:
        'Set when cases should be flagged or escalated for review.',
      frequent_complaints: 'Frequent complaints',
      // AI-33: distinct aria-label for the time-period <select> so it no longer
      // shares the threshold input's 'frequent_complaints' label.
      frequent_period_aria: 'Frequent complaints time period',
      frequent_inline_prefix: 'Escalate cases when',
      frequent_inline_suffix: 'are detected within',
      complaints_suffix: 'complaints',
      frequent_helper:
        'Frequent complaints can indicate potential abuse, or a customer experiencing repeated issues.',
      consecutive_complaints: 'Consecutive complaints',
      consecutive_inline_prefix: 'Escalate cases when',
      consecutive_inline_suffix: 'are detected across recent orders.',
      consecutive_complaints_suffix: 'consecutive complaints',
      consecutive_helper:
        'Repeated issues across recent orders may indicate a pattern worth reviewing.',
      // AI-302 (FR-41): merchant-authored suspected-gaming threshold. Mirrors the
      // FREQUENT block (threshold + period). Copy says "flag for review", never
      // "block" — the brain flags; the merchant reviews and decides to Restrict.
      auto_resolution_gaming: 'Suspected abuse',
      auto_resolution_period_aria: 'Suspected abuse time period',
      auto_resolution_inline_prefix: 'Flag customers after',
      auto_resolution_suffix: 'auto-resolutions',
      auto_resolution_inline_suffix: 'within',
      auto_resolution_helper:
        'Customers who cross this many AI auto-resolutions in the period are flagged for merchant review, not automatically blocked.',
      refund_handling: 'Refund handling',
      refund_handling_desc: 'How RecoveryAI handles refund-related issues.',
      deny_refunds: 'Do not allow refund recommendations',
      deny_refunds_desc:
        'AI will use only the rewards you selected above to offer restitution to consumers.',
      refunds_unavailable_note:
        'RecoveryAI does not issue cash refunds in this phase — it offers restitution using the rewards you selected above.',
      period_7d: '7 days',
      period_14d: '14 days',
      period_30d: '30 days',
      period_60d: '60 days',
      period_90d: '90 days',
    },
    // DATA-5325 T2: a dedicated step split off from Limits. The refund-handling
    // card it hosts still reads its copy from `wizard.limits.*` (it was moved,
    // not re-authored); the escalation-routing card that used to sit beside it
    // was retired by D1, along with its `wizard.limits` copy.
    escalation_routing: {
      title: 'Escalations',
      description:
        "ThanxAI automatically resolves customer's issues for you when it can. When it cannot, it escalates to your staff to help your customer. How would you like to handle these escalations?",
    },
  },
  activity: {
    // Section heading above the escalations table. Key stays `cases_heading`
    // (RAI redesign renamed the label, not the key — avoids jest.mock churn),
    // mirroring the key≠label pattern used elsewhere in this file.
    cases_heading: 'Review Escalations',
    tabs: {
      // Only the parent Escalations tab badge (Landing) survives the single-table
      // redesign; the sub-tab labels were removed with item 5.
      needs_attention_badge_one: '%{count} unresolved escalation',
      needs_attention_badge_other: '%{count} unresolved escalations',
    },
    // Supplied pre-translated to the shared TableToolbar (which words these per
    // surface): the search box's accessible name and the hide-resolved toggle.
    // No placeholder — the escalations search box is intentionally blank.
    search_aria: 'Search escalations',
    hide_resolved_label: 'Hide resolved',
    // Column headers, sort label, feedback-icon labels + retry now come from the
    // shared `components.feedback_table` bundle (the table moved to
    // components/FeedbackTable); only the scene-owned error body stays here.
    error: {
      body: 'Something went wrong loading this activity.',
    },
    // Status pill labels moved to the shared `components.feedback_status_badge`
    // bundle when the badge was promoted to components/FeedbackStatusBadge.
    empty: {
      // Single-table redesign: FeedbackTable shows `default` when no filter is
      // active, `filtered` when any filter is (search OR hide-resolved), so the
      // copy stays accurate for either — hence "filters", not "search".
      default: 'No escalations to review',
      filtered: 'No escalations match your filters',
    },
  },
  // AI-41: the NEW Restricted Customers Settings list (the restrict/block world).
  // Distinct from `whitelisted_customers` (exempt-from-AI, the inverse) — do NOT
  // reuse that block here. Column + badge copy is interim pending the
  // `Restricted Customers/RAI - Settings.png` Figma frame — confirm before launch.
  restricted_customers: {
    title: 'Restricted customers',
    description:
      'Restricted customers may still rate their experience but they will not be prompted to share details about their experience, RecoveryAI will not resolve their issues, and your staff will not be notified. This is primarily useful to manage users who may be abusing the system. You may restrict any customer from the escalation detail for any order.',
    table: {
      caption: 'Restricted customers',
      customer: 'Customer',
      complaints_30d: 'Complaints 30d',
      visits_30d: 'Visits 30d',
      complaint_rate: 'Complaint rate',
      status: 'Status',
      actions: 'Actions',
    },
    badge: {
      restricted: 'Restricted',
    },
    empty: 'No restricted customers found',
    error: {
      body: 'Something went wrong loading restricted customers.',
      retry: 'Try again',
    },
    search_placeholder: 'Search by customer name...',
    // Screen-reader announcement for the live-filtering search results (WCAG
    // 4.1.3). react-redux-i18n does NOT auto-select _one/_other from { count },
    // so the pane picks the form with a `count === 1 ? _one : _other` ternary.
    search_results_announcement_one: '%{count} customer matches your search',
    search_results_announcement_other: '%{count} customers match your search',
    actions: {
      menu_aria: 'Actions for %{customer}',
      view_profile: 'View profile',
      // DATA-4871: no working customer-profile route yet → rendered disabled-with-reason.
      view_profile_disabled_reason: "Customer profiles aren't available yet",
      remove: 'Remove restriction',
    },
    remove: {
      title: 'Remove this restriction?',
      body: 'Remove the restriction on %{name}? They will be eligible for RecoveryAI again.',
      confirm: 'Remove restriction',
      error: 'Failed to remove the restriction. Please try again.',
    },
    pagination: {
      prev: 'Prev',
      next: 'Next',
      page: 'Page',
      of: 'of %{total}',
    },
  },
  // AI-303 (FR-41): suspected-gaming review queue. Distinct from restricted_customers
  // (whitelist/un-restrict) — this queue is review→Restrict only, no whitelist.
  gaming_review: {
    title: 'Suspected abuse review',
    description:
      'Customers who crossed your auto-resolution limit are flagged here for review. RecoveryAI does not block them automatically. Review each customer and restrict them if the pattern looks like abuse.',
    table: {
      caption: 'Customers flagged for suspected abuse',
      customer: 'Customer',
      auto_resolutions: 'Auto-resolutions',
      period: 'Review period',
      actions: 'Actions',
    },
    // Period column value: a fixed phrasing that reads grammatically for any day
    // count (avoids 1-day/N-days pluralization) — e.g. "30-day window".
    period_window: '%{days}-day window',
    empty: 'No customers are flagged for review',
    error: {
      body: 'Something went wrong loading the abuse-review queue.',
      retry: 'Try again',
    },
    restrict: {
      button: 'Restrict',
      action_aria: 'Restrict %{name}',
      // Restrict needs the source conversation to POST; a row without one can't
      // be restricted here, so the action is disabled with this explanation.
      unavailable_aria:
        'Restrict %{name} (unavailable — no source conversation)',
    },
    // AI-303 Unit 7: "N of M" pill comparing the row's auto-resolution count to
    // the merchant's configured limit.
    threshold_badge: {
      of: '%{count} of %{threshold}',
    },
    pagination: {
      prev: 'Prev',
      next: 'Next',
      page: 'Page',
      of: 'of %{total}',
    },
  },
  case_detail: {
    title: 'Case %{id}',
    // Cases now live under the "Escalations" tab (AI-36 renamed the old
    // "Activity" tab); the back link returns there. Just "Back" + an arrow
    // icon (order-rating detail parity, AI-1024) — the destination doesn't
    // need spelling out in the link text.
    back: 'Back',
    order_heading: 'Order #%{number}',
    empty: '—',
    tabs_label: 'Case sections',
    tabs: {
      overview: 'Overview',
      transcript: 'Conversation Transcript',
    },
    ai_summary: 'AI Summary',
    case_details: {
      heading: 'Case details',
      order_number: 'Order number',
      date_time: 'Date & time',
      ordering_location: 'Ordering location',
      order_total: 'Order total',
      items_ordered: 'Items ordered',
      affected_items: 'Affected items',
      // The order-summary strip's own copy (delivery method, item count, "View
      // full receipt") and the receipt modal's live in the shared
      // `components.order_summary` bundle — both components were promoted to
      // src/components for the Reports order-rating detail page.
    },
    timeline: {
      heading: 'Case timeline',
      events: {
        conversation_started: 'Conversation started',
        escalated: 'Escalated',
        escalation_acknowledged: 'Escalation acknowledged',
        escalation_resolved: 'Escalation resolved',
        resolved: 'Resolved',
      },
    },
    // Customer-information labels moved to the shared `components.customer_info`
    // bundle when that panel was promoted to components/CustomerInfoCard for the
    // Reports order-rating detail page.
    // Transcript role labels moved to the shared `components.transcript` bundle
    // when the message list was promoted to components/Transcript.
    resolution: {
      heading: 'AI resolution details',
      type: 'Type',
      status: 'Status',
      cost: 'Estimated cost',
      reward: 'Offer',
      resolved_by: 'Resolved by',
      resolved_at: 'Resolved on',
      // Shown as the "Resolved by" value when the AI closed the case; an
      // operator's name is shown instead when a person resolved it.
      resolved_by_ai: 'RecoveryAI',
      // The resolution-type enum labels moved to
      // `components.recent_activities.resolution_types` with the promoted
      // components/RecentActivitiesTable — the table was their only consumer.
      statuses: {
        pending: 'Pending',
        fulfilled: 'Fulfilled',
        failed: 'Failed',
      },
    },
    escalation: {
      // heading/type/reason/status/types/statuses were the Overview tab's
      // redundant Escalation pane, removed with it (AI-912) — the header
      // already surfaces escalation state via the actions below.
      // Operator actions on the escalation (non-money AASM transitions). Shown on
      // the case-detail header when arrived via the escalation email/queue (the
      // escalation id rides in router state — F3). Acknowledge is a single button
      // (pending only); Resolve opens the modal below.
      actions_heading: 'Escalation',
      acknowledge: 'Acknowledge',
      acknowledge_aria: 'Acknowledge this escalation',
      acknowledge_error:
        'Something went wrong acknowledging this escalation. Please try again.',
      resolve: 'Resolve',
      resolve_aria: 'Resolve this escalation',
      resolve_modal: {
        heading: 'Resolve escalation',
        aria_label: 'Resolve escalation',
        resolution_type_label: 'Resolution type',
        // resolved_by_rai is intentionally NOT offered here — it's an AI outcome,
        // not an operator choice (the wire enum still accepts it).
        resolution_types: {
          resolved_manually: 'Resolved manually',
          unresolved: 'Unresolved',
        },
        notes_label: 'Notes (optional)',
        notes_placeholder: 'Add an optional note…',
        confirm: 'Resolve',
        error:
          'Something went wrong resolving this escalation. Please try again.',
      },
    },
    // The panel's own chrome (heading, column headers, "View", the em-dash and
    // the resolution-type labels) moved to `components.recent_activities` with
    // the promoted components/RecentActivitiesTable. Only the per-row View
    // label stays here — it names a prior CASE, which is this surface's
    // wording; the Reports order-rating detail supplies its own.
    recent_activities: {
      view_aria: 'View case %{order}',
    },
    actions: {
      grant_reward: 'Grant reward',
      send_message: 'Send message',
      mark_resolved: 'Mark as resolved',
      // "this" added for consistency with restrict.heading/.aria_label below,
      // and to match the target overflow-menu design.
      restrict: 'Restrict this customer',
      // aria-label for the "..." overflow trigger that now holds the Restrict
      // action (CaseActionsMenu) — no interpolation needed, unlike
      // restricted_customers.actions.menu_aria's per-row %{customer}, since
      // this is a single page-level trigger.
      menu_aria: 'More actions',
      // Reason shown (title + sr-only aria-describedby, per RestrictedCustomers'
      // RowActionsMenu precedent) when the Restrict item is disabled because
      // the customer is already restricted from a different case.
      restrict_disabled_reason: 'Customer is already restricted.',
    },
    // Confirmation dialog for the operator "Restrict customer" action (Sketch
    // order-feedback-landing-page v83). Restricting blocks the customer AND
    // resolves this case in one step (the backend restricts + auto-resolves
    // atomically — #569). When the customer has OTHER open escalations, the
    // `escalations` panel lets the operator close those out too (checkbox
    // checked by default).
    restrict: {
      heading: 'Restrict this customer',
      subtitle:
        'Prevents RecoveryAI from interacting with this customer in the future. Typically used when you suspect abuse.',
      means_label: 'What this means',
      means: {
        incentives:
          'They will not receive automated recovery incentives via RecoveryAI.',
        feedback:
          'They can still rate orders, but they will not be asked for written feedback.',
        // "This case and future cases", not "their cases": other existing
        // cases close only via the `escalations` panel below (FR-67).
        resolved:
          'This case and their future cases are marked as resolved automatically, and your staff will not be notified.',
      },
      confirm: 'Restrict and resolve',
      aria_label: 'Restrict this customer',
      error:
        'Something went wrong restricting this customer. Please try again.',
      // Shown instead of `error` when the restrict POST 422s specifically
      // because the customer is already restricted (the dominant failure per
      // the plan's grounded findings) — retrying "please try again" copy would
      // just 422 again, so this names the actual state instead.
      already_restricted_error: 'This customer is already restricted.',
      // Shown only when the customer has other open escalations. `%{count}` is
      // always ≥ 1 here (the panel is hidden at 0), and `%{name}` falls back to
      // "This customer" when PII consent is absent. Singular/plural picked at the
      // call site (react-redux-i18n does not auto-pluralize).
      escalations: {
        heading_one: '%{name} has 1 open escalation right now',
        heading_other: '%{name} has %{count} open escalations right now',
        default_subject: 'This customer',
        checkbox: 'Close them out and mark as resolved',
        helper:
          "This resolves every other open escalation without a reward — no one will review them individually first. Future thumbs-downs won't escalate.",
      },
    },
    // The grant-reward dialog's copy moved to the shared
    // `components.grant_reward_modal` bundle when the modal was promoted to
    // components/GrantRewardModal for the Reports order-rating detail page. Only
    // the failure copy stays here (`resolve.error` below) — it names THIS
    // surface's record, and the promoted modal takes it as a prop.
    resolve: {
      // Inline error when the resolve mutation fails (e.g. 422 over-budget or
      // invalid reward). Shared by Grant Reward and Mark Resolved.
      error: 'Something went wrong resolving this case. Please try again.',
    },
    send_message: {
      // Success flash. The send also resolves the case server-side, but the
      // resolve is self-evident (the case drops off the queue) while the send
      // is not — so this names the send specifically to confirm the less-visible
      // effect. Interim "on its way" wording (not "has been sent"): a 200 means
      // the email was enqueued, not delivered — the send happens async and can
      // still fail silently. Revisit once delivery status is exposed.
      success: 'Your message is on its way and the case is resolved.',
      // Inline error when the send-and-resolve mutation fails — distinct from
      // resolve.error above (the bare Mark Resolved path).
      error: 'Something went wrong sending this message. Please try again.',
    },
    loading: 'Loading case…',
    error: 'Something went wrong loading this case.',
    // A BACKGROUND refetch failing over still-good cached data (e.g. a
    // transient 5xx on the refetch that acknowledgeEscalation/resolveEscalation
    // trigger) — shown inline instead of the full-page `error` above, which is
    // reserved for "nothing cached to show".
    refetch_error:
      "We couldn't refresh this case. The information shown may be out of date.",
    retry: 'Try again',
    not_found: "We couldn't find that case.",
  },
  // The /recovery_ai/escalations/:id route is a transparent resolver: it GETs the
  // escalation linkage and redirects to the case detail. It only ever renders a
  // spinner (while resolving) or this not-found state (a dead/invalid link, e.g.
  // an old escalation email). The acknowledge/resolve controls live on the
  // case-detail escalation block (see case_detail.escalation), not here.
  escalation_detail: {
    loading: 'Loading escalation…',
    not_found: "We couldn't find that escalation.",
    back: 'Back to Escalations',
  },
  whitelisted_customers: {
    title: 'Always Allowed Customers',
    empty:
      'No whitelisted customers. Customers will appear here when they exceed your complaint threshold.',
    table: {
      customer: 'Customer',
      complaints: 'Complaints (30 days)',
      last_complaint: 'Last Complaint',
      actions: 'Actions',
    },
    whitelist: 'Whitelist',
    whitelist_permanent: 'Permanent',
    whitelist_temporary: 'Temporary',
    whitelist_allowance: 'Allow %{count} more complaints',
    remove_whitelist: 'Remove whitelist',
    remove_confirm:
      'Remove whitelist for %{name}? Future complaints will be flagged normally.',
    badge_whitelisted: 'Whitelisted',
    badge_whitelisted_remaining: 'Whitelisted (%{remaining} remaining)',
  },
  // WP7 Activation workflow (AI-22 / AI-169 / AI-23 / AI-47). Top-level namespace.
  // A second, unreachable `wizard.activation` block used to shadow it in greps;
  // that one is gone — every `recovery_ai.activation.*` hit is this one.
  activation: {
    summary: {
      // Small green status pill above the header (AI-22 Figma).
      ready_pill: 'Ready to activate',
      header: "%{merchant}'s agent is fully configured to respond to guests",
      header_fallback: 'Your agent is fully configured to respond to guests',
      intro:
        "ThanxAI has calibrated everything from your answers. Activate when you're ready.",
      once_activated: 'Once activated:',
      // The three "once activated" benefit bullets (each rendered with a green check).
      benefits: {
        responses: 'More customers receive responses to their complaints',
        faster: 'Complaints are resolved faster',
        recovery:
          'Faster resolutions lead to higher recovery rates and more repeat purchases',
      },
      // AI-888: the value-proposition clause is bolded (credit_emphasis), the
      // reassurance tail (credit_suffix) stays regular weight. Split into two keys
      // so the bold span wraps only the first clause — see ConfiguredSummary.
      credit_emphasis: '%{amount} in credit, already on your account',
      credit_suffix: ' — so getting started is on us.',
      ready_to_activate: 'Ready to activate',
      notify_admin: 'Ask an admin to activate',
      test_in_sandbox: 'Test your agent in sandbox',
      // AI-1132: an agent with no grantable rewards has nothing to offer during
      // recovery, so the activate CTA is disabled. Shown beside it when the merchant
      // has zero catalog picks AND no non-declined make-good ladder rungs.
      no_rewards_hint:
        'Add at least one reward before activating so your agent has something to offer.',
      // Recoverable error region shown (flag-on) when the config GET fails, instead of
      // redirecting a configured merchant into the onboarding funnel (#4).
      load_error: "We couldn't load your agent's settings.",
      load_error_retry: 'Try again',
    },
    summary_cards: {
      change: 'Change',
      change_aria: 'Change %{item}',
      // Per-card CATEGORY label (the small uppercase text above each setting).
      philosophy: {
        title: 'Recovery generosity',
      },
      brand_voice: {
        title: 'Brand voice',
      },
      active_locations: {
        title: 'Active locations',
      },
      approved_rewards: {
        title: 'Approved rewards',
      },
      escalation: {
        title: 'Escalation contacts',
      },
      // The per-card SETTING (the bold value beneath the category label). Derived
      // from the live recovery config / metrics; `unset` is the graceful fallback
      // shown while a field is still loading (never a fabricated value).
      setting: {
        philosophy: {
          frugal: 'Cautious',
          balanced: 'Balanced',
          generous: 'Generous',
          whatever_it_takes: 'Whatever it takes',
          unset: 'Configured',
        },
        brand_voice: {
          default: 'Default Thanx brand voice',
          custom_text: 'Custom, in your own words',
          unset: 'Configured',
        },
        // react-redux-i18n does NOT auto-pluralize — the call site picks _one/_other.
        active_locations_one: 'Piloting at %{count} location',
        active_locations_other: 'Piloting at %{count} locations',
        active_locations_unset: 'Piloting at selected locations',
        approved_rewards_one:
          '%{count} reward selected from your reward catalog',
        approved_rewards_other:
          '%{count} rewards selected from your reward catalog',
        approved_rewards_unset: 'Rewards selected from your reward catalog',
        escalation_managers_fallback:
          'Location managers, with a fallback recipient',
        escalation_managers: 'Location managers',
        escalation_admins: 'Admins',
        escalation_unset: 'Escalation contacts set',
      },
      // The long descriptive paragraph beneath each card's setting (AI-22 Figma copy).
      description: {
        // AI-1179: the philosophy card's one-liner now reads
        // recovery_ai.wizard.philosophy.<posture>_desc (the single source of
        // truth also used by the wizard/slider), so it can't drift out of
        // sync with the decided posture copy. `philosophy_unset` is the only
        // survivor here — the fallback for a config that hasn't loaded yet.
        // Distinct from summary_cards.setting.philosophy.unset ("Configured")
        // so the unset card doesn't show the same word twice (review nit).
        philosophy_unset: 'Your recovery stance is loading.',
        brand_voice:
          'Acknowledge the issue first. Apologize sincerely. Offer a solution. Use first names. Avoid corporate language. End with optimism.',
        active_locations:
          'RecoveryAI will run at your selected pilot locations. You can turn on more or remove locations anytime.',
        approved_rewards:
          'Selected from your reward catalog and RecoveryAI default discounts based on typical resolution value. Your agent grants rewards from this set when making things right.',
        escalation:
          "When RecoveryAI can't resolve a case, the location manager configured in Thanx is notified to respond. Locations without an assigned manager fall back to admins.",
      },
    },
    live_updates_required: {
      title: 'Turn on live updates first',
      body: 'Live updates are off. Turn them on from the Live updates page to activate.',
      body_lead:
        'RecoveryAI needs guest ratings, and ratings come from live updates.',
      not_now: 'Not now',
      go_to_live_updates: 'Go to Live updates',
    },
    pricing_modal: {
      // Duplicates activation.summary.ready_pill: same words, different surface,
      // asserted independently (see the billing_central note below for the precedent).
      ready_pill: 'Ready to activate',
      title: 'Review and activate RecoveryAI',
      subtitle:
        "Review the configuration and billing terms below, then activate when you're ready.",
      headline: 'No setup fee. No monthly minimum. Pause any time.',
      rates_label: "How you'll be billed",
      escalation_title: 'Escalated to your team',
      escalation_label:
        "When RecoveryAI can't resolve a case and hands it off, you pay nothing.",
      // Capital-I "AI", not a lowercase L.
      resolution_title: 'Per AI Resolution',
      resolution_label: 'You pay only when RecoveryAI fully handles the case',
      recovery_title: 'Per Successful Recovery',
      // 30 days is the confirmed recovery window (settled 2026-08-16).
      recovery_label: 'When a recovered guest returns within 30 days',
      credit: {
        amount_label: 'Free Credits',
        heading: 'Your first %{recoveries} guest recoveries are on us',
        // Deliberately separate from activation.success.credit: same facts, different
        // surface, asserted independently. No plural pair — %{recoveries} can only
        // reach 1 if the combined rate hits the full credit, and success.credit
        // interpolates the same figure without pluralising.
        body: "We've added a %{amount} credit to your account to be used against RecoveryAI fees. That covers your first %{recoveries} guest recoveries. By the time it runs out, we're confident you'll never want to turn it off.",
      },
      agreement: {
        label: 'Activation agreement',
        // Rendered via dangerouslySetInnerHTML (one inline legal link). %{merchant}
        // is lodash-escaped at the call site — react-i18nify does not escape
        // interpolations. %{linkClass} is a styletron class from the component
        // (belizeHole + underline); the design shows this underlined and blue, and
        // bootstrap gives colour but not the underline.
        //
        // The link text is the title of the document it opens
        // (locales/legal/recoveryAiTerms.ts `title`), which is one-word
        // RecoveryAI like all product prose (AI-913). Keep the two in sync.
        body: 'By activating, you confirm you\'re an account admin authorized to incur charges on this Thanx account and agree Thanx will bill %{merchant} at the rates above for RecoveryAI usage starting today. Rewards granted from the approved catalog are issued at standard reward economics — no separate charge from Thanx. You have reviewed and agree to the <a href="%{serviceTermsUrl}" class="%{linkClass}" target="_blank" rel="noopener noreferrer">RecoveryAI Terms of Service</a>.',
        merchant_fallback: 'your account',
      },
      go_back: 'Go back',
      agree_and_activate: 'Agree & activate RecoveryAI',
      submit_error:
        'Something went wrong activating RecoveryAI. Please try again.',
      billing: {
        section_label: 'How RecoveryAI gets billed to you',
        section_help:
          'Your Thanx contract is billed per location. You can choose how RecoveryAI is billed.',
        central: 'Bill RecoveryAI centrally',
        central_help:
          'A single line item on your corporate Thanx invoice. Easiest if marketing covers the cost.',
        per_location: 'Bill RecoveryAI per location',
        per_location_help:
          "Each location's RecoveryAI charges appear on that location's bill. Matches your existing Thanx billing structure.",
      },
    },
    success: {
      // Shown while the activity-metrics query is still resolving (e.g. a direct
      // visit/refresh with a cold cache) so the celebratory page never flashes an
      // em-dash count — the count headline swaps in once metrics load.
      headline_loading: 'RecoveryAI is live!',
      headline_one: 'RecoveryAI is live at %{count} pilot location',
      headline_other: 'RecoveryAI is live at %{count} pilot locations',
      // Shown in the Activated/Authorized-by cells while getActivation is still
      // resolving (cold-cache direct visit/refresh) — an em-dash there would read
      // as "not activated" right after a successful paid activation.
      detail_loading: 'Loading…',
      credit:
        "We've added a %{amount} credit to your account to be used against RecoveryAI fees. That's %{recoveries} guest recoveries on us! By then, we're confident you'll never want to turn it off!",
      detail_activated: 'Activated',
      detail_locations: 'Locations',
      detail_credits: 'Launch credit',
      detail_billing: 'Billing',
      detail_authorized_by: 'Authorized by',
      // react-redux-i18n does not auto-pluralize — the caller picks _one/_other.
      locations_value_one: '%{active} of %{total} location',
      locations_value_other: '%{active} of %{total} locations',
      // 'Central Thanx invoice' is a statement of fact on the landing page; the
      // pricing-modal radio keeps its own 'Central billing' label
      // (pricing_modal.billing.central), asserted independently.
      billing_central: 'Central Thanx invoice',
      billing_per_location: 'Per-location billing',
      billing_rates: '%{resolution}/resolved case, %{recovery}/recovered guest',
      keep_testing: 'Keep testing',
      go_to_dashboard: 'Go to RecoveryAI dashboard',
      // Visually-hidden accessible name for the summary <table> (matches the
      // sibling RAI tables' sr-only <caption> pattern) — labels the table, not
      // the celebratory page heading above it.
      table_caption: 'Activation summary',
    },
  },
  // WP7 preparer (non-admin) handoff modal (AI-23). Top-level namespace.
  preparer: {
    badge: 'Admin approval required',
    // ONE title for both states (settled 2026-08-16): the design's "Share with…"
    // variant was mock drift, not a state fork. Only the per-row control forks.
    title: 'Send to an admin to activate',
    description:
      'Activating RecoveryAI impacts your Thanx billing. An account admin can turn it on. Your configuration is saved.',
    message_label: 'Message',
    // AI-923: the default draft seeded into the editable textarea — the preparer
    // can rewrite it before it is sent to the admin as MESSAGE_FROM_REQUESTER.
    preview_message:
      'Hi! %{name} has set up RecoveryAI on Thanx and needs your review.',
    preview_message_fallback:
      'Hi! A teammate has set up RecoveryAI on Thanx and needs your review.',
    admins_header: 'Admins at %{merchant}',
    admins_header_fallback: 'Admins',
    admins_empty: 'No admins found for this account.',
    // Cause-neutral on purpose: 'unavailable' covers an RBAC 403, a dark-launch
    // 404 while the notify backend is flag-gated off, and a network error alike
    // — naming "permissions" here would be wrong for the other two (Darshan,
    // PR #4012).
    admins_unavailable:
      "We can't show your admin list right now. Copy the activation link below and send it to an admin instead.",
    link_copied: 'Activation link copied',
    link_copy_failed:
      "We couldn't copy the link. Select the link above and copy it manually.",
    notify: 'Notify',
    resend: 'Resend',
    notify_aria: 'Notify %{name}',
    resend_aria: 'Resend to %{name}',
    notified: 'Notified %{time}',
    view_more_one: 'View %{count} more admin',
    view_more_other: 'View %{count} more admins',
    view_less: 'View fewer admins',
    copy_link: 'Copy activation link',
    done: 'Done',
    notify_error: "We couldn't send that notification. Please try again.",
  },
}
